// App.tsx — root. Owns auth gate, loads/saves server-side game state,
// drives the per-turn game loop, and manages the billing status that
// feeds the header balance pill and the Paywall modal.
//
// Auth gate:
//   - On mount, check tokens.getAccessToken().
//     - absent → render <Login />
//     - present → render the game; in parallel fetch /api/save and hydrate.
//
// Save sync:
//   - After each successful turn (applyDelta), PUT /api/save (best-effort).
//     Failures surface a toast but don't block subsequent turns.
//
// Billing:
//   - On mount, GET /api/billing/status alongside /api/save.
//   - Refetch after each successful turn (server has just decremented).
//   - Refetch on tab focus (visibilitychange) — user might come back from
//     Stripe Checkout in the same tab.
//   - 402 from /api/turn (OutOfTurnsError) → open Paywall modal.
//   - When balance crosses below 10 the first time per session → soft toast.
//
// Return URLs (per BILLING_CONTRACT §6):
//   - We use option (b) — query params, not paths.
//   - Stripe Checkout success_url=`${WEB_ORIGIN}/?billing=success`
//     and cancel_url=`${WEB_ORIGIN}/?billing=cancel`.
//   - On mount we detect the param, show a small in-card success/cancel
//     screen, refetch status, and history.replaceState the URL clean.

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type FormEvent,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import i18nInstance, { loadLanguage } from "./i18n";
import {
  makeInitialState,
  applyDelta,
  applyPlayerSetup,
  condenseStateForModel,
  migrateState,
  setGuidanceLevel,
  setSpiceLevel,
  guidanceLabel,
  getWorldGen,
  resolveMetaCommand,
  type GameState,
  type PlayerGender,
  type SequelOption,
} from "./engine";
import {
  askEngine,
  OutOfTurnsError,
  UpstreamCapacityError,
} from "./api";
import { styles } from "./styles";
import Login from "./components/Login";
import Paywall from "./components/Paywall";
import CharacterSetup from "./components/CharacterSetup";
import NamePrompt from "./components/NamePrompt";
import StoryPicker from "./components/StoryPicker";
import { FullMap, TrackingMap } from "./components/Map";
import ScenarioPicker from "./components/ScenarioPicker";
import Bookshelf from "./components/Bookshelf";
import BookReader from "./components/BookReader";
import ReferralsPage from "./components/ReferralsPage";
import AccountSettingsPage from "./components/AccountSettingsPage";
import PlayerIdentityEdit from "./components/PlayerIdentityEdit";
import Finale from "./components/Finale";
import ShareModal from "./components/ShareModal";
import { beginSequel, getSequelOptions } from "./services/booksApi";
import {
  cancelAccountDeletion as cancelAccountDeletionApi,
  getAccountDeletionStatus,
} from "./services/referralsApi";
import { ScenarioProvider } from "./scenarios/ScenarioContext";
import ScenarioThemeWrapper from "./scenarios/ScenarioThemeWrapper";
import { SCENARIO_PRESENTATIONS } from "./scenarios/presentation";
import { useHoverMenu } from "./hooks/useHoverMenu";
import {
  getAccessToken,
  getUser,
  clearAuth,
  setUser as persistAuthUser,
  type AuthUser,
} from "./auth/tokens";
import { logout, getMe, updateLanguage } from "./services/authApi";
import { adminSetSpice } from "./services/adminApi";
import {
  detectBrowserLanguage,
  getPrimaryBrowserLanguageTag,
  consumeFirstVisitDetectionToken,
} from "./i18n/detectLanguage";
import { findSupportedLanguage } from "./i18n/supportedLanguages";
import {
  listStories,
  getStory,
  createStory,
  putStory,
  deleteStory,
  activateStory,
  restartStory,
  renameStory,
  StoriesApiError,
  StoryFinishedError,
  StoryCapReachedError,
  ScenarioLockedError,
  SubscriptionRequiredError,
  type StoryListEntry,
  type StoryStatus,
} from "./services/storiesApi";
import {
  listScenarios,
  type ScenarioListEntry,
} from "./services/scenariosApi";
import { forkPublicBook } from "./public-reader/services/publicBooksApi";
import {
  getBillingStatus,
  openBillingPortal,
  type BillingStatus,
  type BillingSubscription,
} from "./services/billingApi";
import { Howler } from "howler";
import { useAudio } from "./audio/useAudio";

// localStorage key for the mute flag. Default behaviour when absent is
// MUTED (true) — first toggle persists.
const AUDIO_MUTED_KEY = "ember.audio.muted";

function readMutedFromStorage(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(AUDIO_MUTED_KEY);
    // Absent → muted by default. "false" string → unmuted; anything else → muted.
    if (raw === null) return true;
    return raw !== "false";
  } catch {
    return true;
  }
}

function writeMutedToStorage(muted: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AUDIO_MUTED_KEY, muted ? "true" : "false");
  } catch {
    // ignore quota / private-mode failures
  }
}

// Toasts.
//
// Phase 0 had bare `{ id, text }`. Phase 1 introduces action toasts:
// optional click handler + a CTA label that turns the toast body into a
// clickable surface. Action toasts default to a longer TTL (9s vs 4s) so
// the player has time to read + click.
//
// pushToast accepts BOTH the legacy bare-string call and the new
// options-object form. The legacy form is preserved so unrelated callers
// (save-fail, billing) stay one-liners; the new bookshelf wiring passes
// an object with `text` + `action`.

type ToastVariant = "info" | "success" | "warn";

interface Toast {
  id: number;
  text: string;
  variant: ToastVariant;
  /** Optional click handler. When set, the toast renders as a CTA link. */
  action?: { label: string; onClick: () => void };
}

interface PushToastOptions {
  text: string;
  variant?: ToastVariant;
  durationMs?: number;
  action?: { label: string; onClick: () => void };
}

type BootStatus =
  | "ready"
  | "loading save"
  | "loaded saved game"
  | "fresh start"
  | "save unreachable";

// Billing slice held at App level. balance=null while loading; loaded flips
// once the first GET /api/billing/status either resolves or fails (so the
// header doesn't display "turns: —" forever if billing is unreachable).
interface BillingState {
  balance: number | null;
  subscription: BillingSubscription | null;
  loaded: boolean;
}

// The story the user is currently viewing/playing. Always set before the
// game UI mounts (boot tree guarantees this). `name`/`status`/`scenarioId`
// are kept as a small client-side mirror so the UI doesn't have to round-
// trip to the server to render the chrome.
interface CurrentStory {
  id: string;
  scenarioId: string;
  status: StoryStatus;
  name: string;
  /**
   * Forever-default lock state for THIS story under STORIES_CONTRACT §1.
   * True iff the caller is a free user and this story's scenarioId
   * differs from their forever_default_scenario_id. Drives the in-game
   * paywall overlay. Source: GET /api/stories/:id and POST /api/stories.
   */
  requiresSubscriptionToWrite: boolean;
}

// Detected billing return state read from the URL on first mount.
type BillingReturn = "success" | "cancel" | null;

function readBillingReturn(): BillingReturn {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const v = params.get("billing");
    if (v === "success" || v === "cancel") return v;
    return null;
  } catch {
    return null;
  }
}

function clearBillingReturnFromUrl(): void {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("billing");
    const search = url.searchParams.toString();
    const next = url.pathname + (search ? `?${search}` : "") + url.hash;
    window.history.replaceState(null, "", next);
  } catch {
    // ignore
  }
}

// `?policy=1` is the deep-link the paid-ad creatives use to land visitors
// directly on the platform-level policy block ("How Ember Hollow differs")
// inside the LandingOverlay. Same parse + cleanup pattern as the billing
// return params above. Only meaningful pre-auth — once a user is signed
// in we ignore it (the Game branch never renders the LandingOverlay).
function readPolicyDeepLink(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("policy") === "1";
  } catch {
    return false;
  }
}

function clearPolicyDeepLinkFromUrl(): void {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("policy");
    const search = url.searchParams.toString();
    const next = url.pathname + (search ? `?${search}` : "") + url.hash;
    window.history.replaceState(null, "", next);
  } catch {
    // ignore
  }
}

/**
 * Pick a single "thinking" phrase for the per-turn loading indicator.
 *
 * Candidate pool (defensive, in priority order):
 *   1. scenario-flavoured variants for `scenarioId` (active locale), merged with
 *   2. the generic `game:thinkingVariants` pool.
 * Fallback chain if both are missing/empty:
 *   generic variants → the single `game:thinking` string → literal default.
 *
 * `t` is the i18next translate fn (loosely typed so we can read array keys via
 * `returnObjects` without fighting the overloads). Randomness lives here in the
 * React layer — never in the pure engine.
 */
function pickThinkingPhrase(
  t: (key: string, opts?: Record<string, unknown>) => unknown,
  scenarioId: string | null | undefined,
): string {
  const asStringArray = (val: unknown): string[] =>
    Array.isArray(val) ? val.filter((v): v is string => typeof v === "string") : [];

  const generic = asStringArray(
    t("game:thinkingVariants", { returnObjects: true }),
  );
  const scenario = scenarioId
    ? asStringArray(
        t(`scenarios:${scenarioId}.thinkingVariants`, { returnObjects: true }),
      )
    : [];

  const pool = [...scenario, ...generic];
  if (pool.length > 0) {
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // Last-ditch fallbacks: the single legacy string, then a literal.
  const single = t("game:thinking");
  return typeof single === "string" && single.length > 0
    ? single
    : "the world considers";
}

export default function App() {
  // ── Auth gate ──────────────────────────────────────────────────────────
  const [authed, setAuthed] = useState<boolean>(() => Boolean(getAccessToken()));
  const [user, setUser] = useState<AuthUser | null>(() => getUser());

  // `?policy=1` deep-link from external ad creatives: open the
  // LandingOverlay anchored at the "How Ember Hollow differs" block.
  // Captured once on first mount so a state change doesn't re-trigger it,
  // and the query param is stripped from the URL the same way the
  // ?billing= cleanup does. Only honoured when the user is unauthed —
  // the Game branch never mounts the LandingOverlay so there's nothing
  // to open there.
  const [initialPolicyDeepLink] = useState<boolean>(() => readPolicyDeepLink());
  useEffect(() => {
    if (initialPolicyDeepLink) {
      clearPolicyDeepLinkFromUrl();
    }
  }, [initialPolicyDeepLink]);

  // ── Phase B i18n: keep i18next + <html dir/lang> aligned with the
  // ── authenticated user's `language` field. The login screen reads from
  // ── localStorage (Phase A's `ember.lang` key) via the i18next detector
  // ── plugin; once we have a real account language, snap the bundle to
  // ── that one too. Arabic flips document.documentElement.dir = "rtl".
  useEffect(() => {
    const code = user?.language ?? "en";
    void loadLanguage(code);
  }, [user?.language]);

  useEffect(() => {
    // Mirror i18next's resolved language onto <html>. We watch the
    // languageChanged event so manual loadLanguage() calls + the detector
    // both trigger the same update path. SSR-safe via the document guard.
    if (typeof document === "undefined") return;
    function applyDir() {
      const lang = (i18nInstance.language ?? "en").toLowerCase();
      document.documentElement.lang = lang;
      document.documentElement.dir = lang.startsWith("ar") ? "rtl" : "ltr";
    }
    applyDir();
    i18nInstance.on("languageChanged", applyDir);
    return () => {
      i18nInstance.off("languageChanged", applyDir);
    };
  }, []);

  const handleSignedIn = useCallback((u: AuthUser) => {
    setUser(u);
    setAuthed(true);
  }, []);

  const handleSignOut = useCallback(async () => {
    await logout();
    clearAuth();
    setUser(null);
    setAuthed(false);
  }, []);

  // Phase A i18n — patch a subset of AuthUser fields and persist the
  // merged object back into localStorage so reloads see the latest values.
  // Today this is used by the language picker (Settings + UserMenu) and
  // the first-visit detection effect. Keeps the cache + React state in
  // lockstep.
  const patchUser = useCallback((patch: Partial<AuthUser>) => {
    setUser((current) => {
      if (!current) return current;
      const next: AuthUser = { ...current, ...patch };
      try {
        // Persist the merged object to localStorage so reloads see the
        // latest values without an extra /me round-trip. Aliased import
        // because the React setter in this scope is also called `setUser`.
        persistAuthUser(next);
      } catch {
        // ignore — best-effort persistence
      }
      return next;
    });
  }, []);

  if (!authed) {
    // Wrap the login surface in a ScenarioProvider so the scenario picker
    // (pill row + hover preview) can drive the look of the login screen.
    // Intentionally NOT wrapped around the post-login Game branch — the
    // in-game palette belongs to the user's actual saved story.
    return (
      <ScenarioProvider>
        <Login
          onSignedIn={handleSignedIn}
          initialOverlayOpen={initialPolicyDeepLink}
          initialOverlayScrollTo={
            initialPolicyDeepLink ? "how-ember-hollow-differs" : undefined
          }
        />
      </ScenarioProvider>
    );
  }

  return (
    <Game
      user={user}
      onSignOut={handleSignOut}
      onPatchUser={patchUser}
      key={user?.id || "anon"}
    />
  );
}

// ── Game (the existing UI, plus server-side save sync + billing) ─────────

function Game({
  user,
  onSignOut,
  onPatchUser,
}: {
  user: AuthUser | null;
  onSignOut: () => void;
  /**
   * Phase A i18n — apply a partial AuthUser patch in App state + persist
   * the merged object to localStorage. Today the language picker and
   * first-visit detection effect call it to set `language`.
   */
  onPatchUser: (patch: Partial<AuthUser>) => void;
}) {
  const { t } = useTranslation(["game", "common", "referrals", "scenarios", "bookshelf"]);
  const [state, setState] = useState<GameState>(makeInitialState);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // The rotating "thinking" phrase shown while a turn is generating. Chosen
  // ONCE per turn (in handleSubmit) so it stays stable through the wait but
  // re-rolls for each new turn. Empty string => render falls back to a fresh
  // pick at render time (covers any path that flips `busy` without going
  // through handleSubmit).
  const [thinkingPhrase, setThinkingPhrase] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [bootStatus, setBootStatus] = useState<BootStatus>("loading save");
  const [saveLoaded, setSaveLoaded] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [billing, setBilling] = useState<BillingState>({
    balance: null,
    subscription: null,
    loaded: false,
  });
  const [showPaywall, setShowPaywall] = useState(false);
  // Trigger context for the Paywall — drives the eyebrow/title pair only.
  // Defaults to "manual" (user-chip "Buy turns" or pill click); reset to
  // "manual" whenever the Paywall is dismissed so the next manual open
  // doesn't reuse a stale story-locked title.
  const [paywallReason, setPaywallReason] =
    useState<import("./components/Paywall").PaywallReason>("manual");
  const [billingReturn, setBillingReturn] = useState<BillingReturn>(() =>
    readBillingReturn()
  );

  // ── Multi-story slices ─────────────────────────────────────────────────
  // currentStory: the story the game UI is bound to. null while booting or
  //   when the user is at the boot-time picker gate.
  // allStories: the full list backing the StoryPicker. Refreshed after
  //   create / delete / switch so the rows reflect server truth.
  // scenarioCatalogue: cached on first open of the ScenarioPicker.
  // showStoryPicker / showScenarioPicker: drive the picker overlays.
  //   showStoryPicker doubles as boot gate (when currentStory is null) and
  //   mid-game switch surface (when currentStory is set).
  const [currentStory, setCurrentStory] = useState<CurrentStory | null>(null);
  const [allStories, setAllStories] = useState<StoryListEntry[]>([]);
  const [scenarioCatalogue, setScenarioCatalogue] = useState<
    ScenarioListEntry[]
  >([]);
  const [showStoryPicker, setShowStoryPicker] = useState(false);
  const [showScenarioPicker, setShowScenarioPicker] = useState(false);

  // Tracks the player's most-recently-seen identity across stories. Captured
  // from any hydrated story whose state.player.setupComplete is true, and
  // from any CharacterSetup submission. Used to pre-fill CharacterSetup
  // when the user starts a new story so returning players don't have to
  // re-make the same choice every time.
  const [lastKnownIdentity, setLastKnownIdentity] = useState<{
    gender: PlayerGender;
    drawnTo: Exclude<PlayerGender, null>[] | null;
    age?: number;
    drawnToAgeMin?: number;
    drawnToAgeMax?: number;
  } | null>(null);

  // Sticky guidance preference across stories. Captured from any hydrated
  // story whose state.guidance.level is a valid integer, and from any
  // CharacterSetup submission. Falls back to +2 (Story Mode) at the
  // CharacterSetup site when null — matches the engine's makeInitialState
  // default. Stored separately from lastKnownIdentity because it isn't
  // really part of the player's identity; it's a UX preference.
  const [lastKnownGuidance, setLastKnownGuidance] = useState<number | null>(
    null,
  );

  // True from the moment a new story is created via the ScenarioPicker
  // until the user has either submitted a name in NamePrompt or skipped
  // it. While true (and after CharacterSetup is complete) we render the
  // NamePrompt branch instead of the game UI. Existing-story loads, story
  // switches, and the per-story `restart` command do NOT set this flag —
  // it's strictly a new-story-creation marker.
  const [awaitingName, setAwaitingName] = useState(false);

  // ── Bookshelf slices ──────────────────────────────────────────────────
  // showBookshelf: top-level Bookshelf overlay (a full-page surface).
  // bookReader: when non-null, the BookReader overlay is rendered for
  //   that story. Holds the story id + the chapter to open. Surfaced from
  //   the bookshelf row's "Read" action and from clicking a chapter toast.
  const [showBookshelf, setShowBookshelf] = useState(false);
  const [bookReader, setBookReader] = useState<{
    storyId: string;
    chapterNumber: number;
    /**
     * When true, the reader shows per-chapter "Continue the story from here"
     * branch buttons (private fork). Set by the Finale's "Continue from a
     * point" action, which opens the author's OWN story in branch mode.
     */
    branchEnabled?: boolean;
  } | null>(null);
  // finaleShareStoryId: when non-null, the existing ShareModal is rendered
  // for that story, opened from the inline Finale panel's "Share" / Facebook
  // actions. Reuses the same component the Bookshelf uses (copy link / native
  // share / publish via shareStory()).
  const [finaleShareStoryId, setFinaleShareStoryId] = useState<string | null>(
    null,
  );
  // Phase 3b — the "next book" slate for the FINISHED story currently showing
  // its Finale. Populated two ways: (a) captured from the winning /api/turn
  // response (held in hand), or (b) fetched via getSequelOptions when a
  // previously-finished story is reopened (the original response is gone).
  // We hold the FULL SequelOption objects (engine shape) so onBeginSequel can
  // POST the chosen one to /api/stories/:id/sequel — the Finale card only
  // renders the id/structure/title/pitch subset.
  const [finaleSequelOptions, setFinaleSequelOptions] = useState<SequelOption[]>(
    [],
  );
  // The story id whose persisted slate we last fetched (or hold in hand), so
  // the reopened-finale effect fetches at most once per finished story.
  const [sequelOptionsStoryId, setSequelOptionsStoryId] = useState<
    string | null
  >(null);
  // The option id currently spawning (busy state on its card), or null.
  const [beginningSequelId, setBeginningSequelId] = useState<string | null>(
    null,
  );
  // Phase 3 — referrals dashboard + account-settings (delete account) overlays.
  // Opened from the user-chip menu. Both are full-screen modal-ish overlays.
  const [showReferrals, setShowReferrals] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  // Player-identity edit overlay (gender, drawnTo, age, drawn-to age
  // range). Opens from the user-chip menu. Like AccountSettingsPage,
  // it's a full-screen modal-ish overlay.
  const [showPlayerIdentity, setShowPlayerIdentity] = useState(false);
  // Persistent "deletion scheduled" banner. Lifted on mount via the
  // /api/account/deletion-status fetch; user can revert from the banner.
  const [pendingDeletionAt, setPendingDeletionAt] = useState<string | null>(
    null,
  );
  // `lastSeenChapterAt` is the ISO timestamp the client passes back to
  // /api/turn so the server can compute newChaptersSinceLastTurn. Held in
  // a ref so a turn submission reads the current value without re-render
  // churn. Initialised on mount and bumped after every chapter we toast.
  const lastSeenChapterAt = useRef<string>(new Date().toISOString());

  // Index in state.log of the narration entry that just arrived this turn
  // and should animate word-by-word. Null = no fresh narration; nothing
  // animates. Set when the API returns successfully (handleSubmit's success
  // path), cleared on: next turn submit, click-to-skip, story switch.
  // This is a single-shot flag — the OLD watermark approach (turnCount > N)
  // re-fired the previous narration during the optimistic-update gap before
  // the new one arrived; an explicit index flag avoids that race.
  const [freshNarrationIdx, setFreshNarrationIdx] = useState<number | null>(
    null,
  );

  // ── Phase A audio state ────────────────────────────────────────────────
  // muted: persisted to localStorage; MUTED by default on first visit. The
  // user must opt in via the speaker icon in the header.
  // lastSfx: encodes the latest model-emitted `playSfx` as `${tag}:${turn}`
  // so identical tags on consecutive turns still fire (the nonce changes).
  const [muted, setMuted] = useState<boolean>(readMutedFromStorage);
  const [lastSfx, setLastSfx] = useState<string | null>(null);

  // Position C — multi-room transit. When the model emits a validated
  // `movementPath` on a turn (an ordered list of room ids the player
  // traversed), we hold it here so the map can step-animate through each
  // room rather than snap to the destination. Cleared on each new turn
  // submit; set in the success path after applyDelta if validation passes.
  // Threaded to FullMap + TrackingMap as an optional prop.
  const [pendingMovementPath, setPendingMovementPath] = useState<
    string[] | null
  >(null);

  useAudio({
    currentAmbient: state.currentAmbient ?? null,
    currentSporadic: state.currentSporadic ?? null,
    lastSfx,
    muted,
  });

  const toggleMuted = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      writeMutedToStorage(next);
      // When unmuting, resume the AudioContext if browser is still
      // holding it suspended (this counts as a user gesture).
      if (!next) {
        try {
          const ctx = Howler.ctx;
          if (ctx && ctx.state === "suspended") void ctx.resume();
        } catch {
          // ignore
        }
      }
      return next;
    });
  }, []);

  const logRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const toastSeq = useRef(1);
  const lowBalanceWarned = useRef(false);

  // ── Toasts ──────────────────────────────────────────────────────────────
  // Accepts either a bare string (legacy callers) or an options object.
  // Bare string → info toast at 4s. Options form supports variant, custom
  // TTL, and an optional action (label + onClick) that turns the toast
  // into a clickable CTA.
  const pushToast = useCallback(
    (textOrOptions: string | PushToastOptions, ttlMs?: number) => {
      const id = toastSeq.current++;
      let entry: Toast;
      let duration: number;
      if (typeof textOrOptions === "string") {
        entry = { id, text: textOrOptions, variant: "info" };
        duration = ttlMs ?? 4000;
      } else {
        entry = {
          id,
          text: textOrOptions.text,
          variant: textOrOptions.variant ?? "info",
          action: textOrOptions.action,
        };
        // Action toasts default longer so the user has time to tap.
        duration =
          textOrOptions.durationMs ??
          (textOrOptions.action ? 9000 : 4000);
      }
      setToasts((t) => [...t, entry]);
      setTimeout(() => {
        setToasts((t) => t.filter((x) => x.id !== id));
      }, duration);
    },
    [],
  );

  // ── Bookshelf toast pipeline ─────────────────────────────────────────
  //
  // Three copy variants per dispatch spec:
  //   1. backfill batch → "<N> chapters added to your bookshelf from your
  //      existing <story> story. Tap to read it back."
  //   2. first chapter ever for a story → "Your bookshelf just got its
  //      first book. <story> is now growing into a chaptered novel as you
  //      play. Tap to open your bookshelf."
  //   3. subsequent chapters → "Chapter N: '<Title>' is ready for your
  //      <story> story. <rotating close>." Rotation key is chapterNumber % 4.
  //
  // All toasts wire `action` to open the BookReader. The close-phrase
  // language references sharing in anticipation of Phase 2 — Phase 1's
  // tap-action goes to the reader (share button there is disabled).
  const notifyChapter = useCallback(
    (c: import("./api").NewChapterRef) => {
      const story = allStories.find((s) => s.id === c.storyId);
      const storyName = story?.name || t("common:narration.silence", { defaultValue: "your story" });
      const openReader = () => {
        setBookReader({
          storyId: c.storyId,
          chapterNumber: c.chapterNumber,
        });
        setShowBookshelf(false);
      };

      let text: string;
      if (c.isBackfill) {
        const count = c.chapterCount ?? 1;
        text = t("referrals:chapterToasts.backfill", {
          count,
          story: storyName,
        });
      } else if (c.isFirst) {
        text = t("referrals:chapterToasts.firstChapter", { story: storyName });
      } else {
        // `closes` is a translated 4-element array — index by chapterNumber % 4
        // so identical close phrases rotate as the player accumulates chapters.
        const closes = t("referrals:chapterToasts.closes", {
          returnObjects: true,
          defaultValue: [] as string[],
        }) as string[];
        const close = closes[c.chapterNumber % Math.max(1, closes.length)] ?? "";
        text = t("referrals:chapterToasts.chapter", {
          number: c.chapterNumber,
          title: c.title,
          story: storyName,
          close,
        });
      }

      pushToast({
        text,
        variant: "success",
        action: { label: t("common:buttons.open"), onClick: openReader },
      });
    },
    [allStories, pushToast, t],
  );

  // ── Referral bonus toast pipeline (Phase 3) ───────────────────────────
  //
  // Each successful payout queues a `PendingReferralToast` in Redis; the
  // server lifts the list onto the /api/turn response as
  // `newReferralBonusesSinceLastTurn`. We render:
  //
  //   - one action toast per entry when <=3 land in this batch
  //   - a coalesced "N referral bonuses just landed" toast when >3
  //
  // Either way the tap action opens the Referrals dashboard.
  const notifyReferralBonuses = useCallback(
    (bonuses: import("./api").ReferralBonusToast[]) => {
      if (bonuses.length === 0) return;
      const openReferrals = () => setShowReferrals(true);

      if (bonuses.length > 3) {
        const totalTurns = bonuses.reduce(
          (sum, b) => sum + b.turnsDeposited,
          0,
        );
        pushToast({
          text: t("referrals:bonusToasts.batchSummary", {
            count: bonuses.length,
            turns: totalTurns,
          }),
          variant: "success",
          action: { label: t("common:buttons.view"), onClick: openReferrals },
        });
        return;
      }

      for (const b of bonuses) {
        const text =
          b.reason === "signup"
            ? t("referrals:bonusToasts.signup", {
                name: b.refereeFirstName,
                turns: b.turnsDeposited,
              })
            : t("referrals:bonusToasts.renewal", {
                name: b.refereeFirstName,
                turns: b.turnsDeposited,
              });
        pushToast({
          text,
          variant: "success",
          action: { label: t("common:buttons.view"), onClick: openReferrals },
        });
      }
    },
    [pushToast, t],
  );

  // ── Billing status fetcher ────────────────────────────────────────────
  // Centralised so mount, post-turn, tab-focus, and "refresh status" all
  // share the same path. Errors are quietly logged; the header just keeps
  // showing the last-known balance (or "—" if we never got one).
  const refreshBilling = useCallback(async () => {
    try {
      const status: BillingStatus = await getBillingStatus();
      setBilling({
        balance: status.balance,
        subscription: status.subscription,
        loaded: true,
      });
      // Soft warning: fire once per session when the displayed balance
      // crosses below 10. We compare the freshly-fetched value rather than
      // intercepting the previous balance, because that's the value the
      // header is about to display.
      if (
        !lowBalanceWarned.current &&
        status.balance > 0 &&
        status.balance < 10
      ) {
        lowBalanceWarned.current = true;
        pushToast(t("common:toasts.lowBalance"), 6000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[billing] status failed:", msg);
      setBilling((b) => ({ ...b, loaded: true }));
    }
  }, [pushToast, t]);

  // ── Hydrate stories + billing status on mount ──────────────────────────
  // Boot decision tree (STORIES_CONTRACT §8 / dispatch spec):
  //   1. GET /api/stories.
  //   2. 0 stories → forced ScenarioPicker (defensive — signup creates one).
  //   3. activeStoryId set → hydrate it via GET /api/stories/:id.
  //   4. activeStoryId null && exactly 1 story → hydrate that one + activate
  //      it on the server so the pointer matches the client.
  //   5. activeStoryId null && multiple stories → StoryPicker as boot gate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listStories();
        if (cancelled) return;
        setAllStories(list.stories);

        if (list.stories.length === 0) {
          // No stories — force the scenario picker as a gate. The user
          // can't get to the game until they pick a scenario.
          setShowScenarioPicker(true);
          // Pre-fetch the catalogue so the gate has cards to show. Done
          // sequentially here so an empty state isn't flashing "Loading…"
          // any longer than necessary.
          try {
            const cat = await listScenarios();
            if (cancelled) return;
            setScenarioCatalogue(cat.scenarios);
          } catch (err) {
            if (cancelled) return;
            const msg = err instanceof Error ? err.message : String(err);
            console.warn("[scenarios] load failed:", msg);
            pushToast(t("common:toasts.scenariosFailed", { message: shortErr(msg) }));
          }
          setBootStatus("fresh start");
          return;
        }

        // Determine which story to hydrate.
        let targetId: string | null = list.activeStoryId;
        let needsActivate = false;
        if (!targetId) {
          if (list.stories.length === 1) {
            // Single story but the server pointer is null. Hydrate it and
            // flip the pointer so subsequent /api/turn calls find it.
            targetId = list.stories[0]!.id;
            needsActivate = true;
          } else {
            // Multiple stories, no active pointer — let the user pick.
            setShowStoryPicker(true);
            setBootStatus("fresh start");
            return;
          }
        }

        const { story } = await getStory(targetId);
        if (cancelled) return;
        if (needsActivate) {
          // Best-effort; if it fails, the user can still play this story
          // (the server will use last-known active or 400 — but with one
          // story we expect this to succeed).
          try {
            await activateStory(targetId);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.warn("[stories] activate failed:", msg);
          }
          if (cancelled) return;
        }
        setCurrentStory({
          id: story.id,
          scenarioId: story.scenarioId,
          status: story.status,
          name: story.name,
          requiresSubscriptionToWrite: story.requiresSubscriptionToWrite,
        });
        // migrateState patches in any fields the loaded state predates
        // (player identity, npcs, guidance, world topology) so the engine
        // reads cleanly. scenarioId routes legacy state.layer onto the
        // right D.L axis for the story's layerSemantics.
        const migrated = migrateState(story.state, story.scenarioId);
        setState(migrated);
        // Capture identity from the hydrated story so a subsequent new-
        // story flow can pre-fill CharacterSetup. Only valid once the
        // player has completed setup at least once. Age + drawn-to age
        // range pre-fill the new CharacterSetup fields too (still
        // editable; range defaults to 17/90 when absent).
        if (migrated.player.setupComplete) {
          setLastKnownIdentity({
            gender: migrated.player.gender,
            drawnTo: migrated.player.drawnTo,
            age: migrated.player.age,
            drawnToAgeMin: migrated.player.drawnToAgeMin,
            drawnToAgeMax: migrated.player.drawnToAgeMax,
          });
        }
        // Sticky guidance preference. Captured regardless of setupComplete
        // — even a story still on the CharacterSetup gate carries a valid
        // guidance level (the engine default or a previous selection).
        if (
          migrated.guidance &&
          typeof migrated.guidance.level === "number" &&
          Number.isInteger(migrated.guidance.level)
        ) {
          setLastKnownGuidance(migrated.guidance.level);
        }
        setBootStatus("loaded saved game");
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[stories] load failed:", msg);
        setBootStatus("save unreachable");
        pushToast(t("common:toasts.storyServiceFailed", { message: shortErr(msg) }));
      } finally {
        if (!cancelled) setSaveLoaded(true);
      }
    })();

    // Fire billing fetch in parallel with story load.
    void refreshBilling();

    // Phase 3 — also fetch the account-deletion status so a session that
    // resumes mid-cooldown shows the persistent banner without waiting
    // for the user to open Account Settings.
    void (async () => {
      try {
        const res = await getAccountDeletionStatus();
        if (cancelled) return;
        if (res.pending && res.scheduledAt) {
          setPendingDeletionAt(res.scheduledAt);
        }
      } catch (err) {
        // Non-fatal — banner just won't show.
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[account] deletion-status failed:", msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pushToast, refreshBilling, t]);

  // ── Phase A i18n — first-visit language detection ────────────────────
  //
  // Signed-in users only. Fires AT MOST once per page load
  // (consumeFirstVisitDetectionToken survives StrictMode's double-mount).
  // The decision tree:
  //
  //   1. user.language already set → no action; they've chosen.
  //   2. user.language is null AND a supported non-English browser
  //      language is detected →
  //        - PUT /api/auth/language with the detected code
  //        - patch local state so dependent surfaces (ScenarioPicker
  //          override default, Settings dropdown) reflect immediately
  //        - show a soft toast in the target language: "Language: {Native}.
  //          Tap to change." — opens AccountSettings.
  //   3. user.language is null AND the browser's primary language is
  //      English-or-unsupported → no toast for English (no-op). For an
  //      unsupported language we leave the column null and show a small
  //      "<lang> isn't supported yet — playing in English" toast once.
  //
  // The toast text intentionally lives in English here — Phase A doesn't
  // yet ship translated UI. Phase B's i18n pass will localise it.
  useEffect(() => {
    if (!user) return;
    if (user.language !== null && user.language !== undefined) return;
    if (!consumeFirstVisitDetectionToken()) return;

    const detected = detectBrowserLanguage();
    if (detected && detected !== "en") {
      const entry = findSupportedLanguage(detected);
      const nativeName = entry?.nativeName ?? detected;
      // Fire-and-forget — even if the PUT fails (network blip), the toast
      // and local-state patch are still useful; the next manual change
      // in Settings will resync. We DO patch local state on success so
      // the UI doesn't lag behind the persisted value.
      void (async () => {
        try {
          const res = await updateLanguage(detected);
          onPatchUser({ language: res.language });
          // Load + switch the i18n bundle to the detected language so the
          // confirming toast (and the rest of the UI) renders in-language
          // straight away. Best-effort; missing-bundle warnings are
          // already handled inside loadLanguage.
          await loadLanguage(detected);
          pushToast({
            text: t("common:toasts.languageDetectedHint", { name: nativeName }),
            variant: "info",
            durationMs: 6000,
            action: {
              label: t("common:buttons.change"),
              onClick: () => setShowAccountSettings(true),
            },
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn("[i18n] auto language set failed:", msg);
        }
      })();
      return;
    }

    // Browser preference is English-or-unsupported. For English we're
    // already there — no toast. For unsupported, surface a one-time
    // dismissable note naming the language the user actually asked for.
    if (detected === null) {
      const primaryTag = getPrimaryBrowserLanguageTag();
      if (primaryTag && primaryTag !== "en") {
        // Use the browser's own Intl.DisplayNames when available to give
        // the user a friendly name for their unsupported language. Falls
        // back to the raw tag.
        let displayed = primaryTag;
        try {
          if (typeof Intl !== "undefined" && Intl.DisplayNames) {
            const dn = new Intl.DisplayNames(["en"], { type: "language" });
            const resolved = dn.of(primaryTag);
            if (resolved && resolved !== primaryTag) displayed = resolved;
          }
        } catch {
          // ignore — keep raw tag
        }
        pushToast({
          text: t("common:toasts.languageUnsupported", { name: displayed }),
          variant: "info",
          durationMs: 7000,
        });
      }
    }
    // user.language stays null in either case (English-default behaviour).
  }, [user, pushToast, onPatchUser, t]);

  // ── Phase A i18n — hydrate users.language from the server on mount ───
  //
  // The localStorage cache (set by setupAccount / passkey verify) may
  // pre-date Phase A and therefore lack the `language` field. A fresh
  // /api/auth/me call fills it in so subsequent renders (and the
  // detection effect above) see the canonical value. If `language` is
  // already present we still fetch — best-effort cache freshness — but
  // skip the patch when nothing changed.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void (async () => {
      try {
        const { user: fresh } = await getMe();
        if (cancelled) return;
        if (
          fresh.language !== user.language ||
          fresh.foreverDefaultScenarioId !== user.foreverDefaultScenarioId ||
          fresh.isAdmin !== user.isAdmin
        ) {
          onPatchUser({
            language: fresh.language,
            foreverDefaultScenarioId: fresh.foreverDefaultScenarioId,
            isAdmin: fresh.isAdmin,
          });
        }
      } catch (err) {
        // /me failure is non-fatal — the rest of the app still works on
        // the cached AuthUser. Just log.
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[auth/me] hydrate failed:", msg);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally only depends on user.id — we don't want to refetch
    // every time the local user object changes (e.g. when WE patch it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, onPatchUser]);

  // Existing stories are always playable by their owner under the current
  // scenario-lock semantics (STORIES_CONTRACT §1). The previous in-game
  // paywall overlay + subscription-status re-sync effect have been removed
  // — `currentStory.requiresSubscriptionToWrite` is always false on
  // existing stories today. The `story_locked` paywall reason is still
  // legitimate but only fires on the create-story path (see
  // `handlePickScenario`), not on existing-story load.

  // ── Refetch billing on tab focus (user might return from Stripe) ───────
  useEffect(() => {
    function onVisibility() {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        void refreshBilling();
      }
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
      return () =>
        document.removeEventListener("visibilitychange", onVisibility);
    }
    return undefined;
  }, [refreshBilling]);

  // ── Auto-scroll log + focus management ─────────────────────────────────
  // Default: snap to the bottom on log/busy change. Suppressed when a fresh
  // narration is animating in — the smooth-scroll effect below drives that
  // case instead, syncing scroll position to the word-reveal pace so the
  // user reads in lockstep with the reveal rather than staring at unrendered
  // (opacity-0) text below the fold.
  useEffect(() => {
    if (freshNarrationIdx !== null) return;
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [state.log, busy, freshNarrationIdx]);

  // ── Smooth scroll synced to the word-by-word reveal ────────────────────
  // When a fresh narration arrives, interpolate scrollTop from current to
  // final over the same duration the word reveal will run for. Linear
  // ramp with a soft ease-out at the tail so the last line lands gently.
  // Aborted on user wheel/touch (they chose their own position) or on the
  // next freshNarrationIdx change (next turn / skip / story switch).
  useEffect(() => {
    if (freshNarrationIdx === null) return;
    const pane = logRef.current;
    if (!pane) return;

    const text = state.log[freshNarrationIdx]?.text ?? "";
    const words = text.split(/\s+/).filter(Boolean);
    const hardPunct = (text.match(/[.!?]/g) ?? []).length;
    const softPunct = (text.match(/[,;:]/g) ?? []).length;
    const duration =
      words.length * WORD_STAGGER_MS +
      hardPunct * PAUSE_AFTER_HARD_PUNCT_MS +
      softPunct * PAUSE_AFTER_SOFT_PUNCT_MS +
      WORD_FADE_DURATION_MS;

    const start = pane.scrollTop;
    const end = pane.scrollHeight - pane.clientHeight;
    if (end <= start + 2) return; // already at bottom (or no overflow)

    let raf = 0;
    let userInterrupted = false;
    const startTime = performance.now();

    function tick(now: number) {
      if (userInterrupted || !pane) return;
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      // Ease-out so it decelerates softly at the end. Roughly t^0.85.
      const eased = 1 - Math.pow(1 - t, 1.6);
      pane.scrollTop = start + (end - start) * eased;
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    function onUserScroll() {
      userInterrupted = true;
      cancelAnimationFrame(raf);
    }
    pane.addEventListener("wheel", onUserScroll, { passive: true });
    pane.addEventListener("touchmove", onUserScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      pane.removeEventListener("wheel", onUserScroll);
      pane.removeEventListener("touchmove", onUserScroll);
    };
  }, [freshNarrationIdx, state.log]);

  useEffect(() => {
    if (!busy && inputRef.current) inputRef.current.focus();
  }, [busy]);

  // Story switch / boot — clear any pending fresh-narration flag so the
  // incoming story's historical log doesn't trigger an animation.
  useEffect(() => {
    setFreshNarrationIdx(null);
  }, [currentStory?.id]);

  // Click anywhere on the log pane to skip the in-flight reveal. Clearing
  // the index makes animate=false on next render; the CSS keyframes have
  // `forwards` fill-mode so already-revealed words stay visible.
  const skipNarrationAnimation = useCallback(() => {
    setFreshNarrationIdx(null);
  }, []);

  // ── Best-effort save: fire & forget per turn ───────────────────────────
  // Writes to PUT /api/stories/:currentStoryId. If the story has been
  // finalised on the server (409 story_finished), bounce the user to the
  // StoryPicker — they can't continue editing a frozen story. Other errors
  // surface as a toast and the local state stays as-is.
  const persist = useCallback(
    async (next: GameState) => {
      const story = currentStory;
      if (!story) {
        // Defensive — should never happen during normal gameplay because
        // we don't render the game UI without a story.
        console.warn("[stories] put skipped: no current story");
        return;
      }
      // Existing stories are always writable by their owner under the
      // current scenario-lock rules (STORIES_CONTRACT §1) — turn balance
      // is the only write-gate, and that lives on /api/turn. PUT
      // /api/stories/:id no longer returns 402 for scenario lock.
      try {
        await putStory(story.id, next);
      } catch (err) {
        if (err instanceof StoryFinishedError) {
          pushToast(t("common:toasts.storyFinalised"));
          setShowStoryPicker(true);
          return;
        }
        const msg =
          err instanceof StoriesApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        console.warn("[stories] put failed:", msg);
        pushToast(t("common:toasts.saveFailed", { message: shortErr(msg) }));
      }
    },
    [currentStory, pushToast, t]
  );

  // ── Map pop-out: BroadcastChannel + popped-window tracking ─────────────
  //
  // The map can be opened in a separate browser window (second-monitor use)
  // via `?map=<storyId>` on the same origin. State stays live across both
  // windows over a same-origin BroadcastChannel.
  //
  // - broadcastChannelRef holds the channel for the active story. Re-bound
  //   whenever currentStory.id changes so the message stream is scoped to
  //   one story at a time.
  // - popoutRef holds the Window proxy returned by window.open. We poll
  //   .closed every 2s to clean the reference up when the user shuts the
  //   pop-out; that lets the next "open map" click re-create the in-app
  //   modal naturally.
  // - When the user re-clicks "map" while a pop-out is alive, we focus the
  //   existing window instead of duplicating it. Two map views fighting
  //   over the same state would be confusing for everyone.
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const popoutRef = useRef<Window | null>(null);
  // Tick state so the .closed poller can re-run when a pop-out is opened.
  // The interval itself only fires while a pop-out is referenced; the tick
  // bump triggers the useEffect that owns the interval.
  const [popoutTick, setPopoutTick] = useState(0);

  // Bind the BroadcastChannel to the current story. Listens for
  // "request_state" pings from a freshly-opened pop-out and replies with
  // the current state immediately — covers the case where the server's
  // last-saved blob is one turn behind the in-memory state.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    if (!currentStory?.id) return;
    const channel = new BroadcastChannel(`ember-map-${currentStory.id}`);
    channel.onmessage = (e: MessageEvent) => {
      // Pop-out asks for current state on subscribe — push it. We don't
      // use `state` from closure here because that snapshots at effect-
      // bind time; broadcastChannelRef.current is read at message time so
      // the latest state push below already covers normal turn updates.
      // For request_state replies we still need the up-to-date state, so
      // we rely on the secondary effect that broadcasts on every change
      // PLUS this one-shot reply that uses a ref to the current state.
      if (e.data?.type === "request_state") {
        const cur = latestStateRef.current;
        if (cur) channel.postMessage({ type: "state", state: cur });
      }
    };
    broadcastChannelRef.current = channel;
    return () => {
      channel.close();
      if (broadcastChannelRef.current === channel) {
        broadcastChannelRef.current = null;
      }
    };
  }, [currentStory?.id]);

  // Keep a ref to the latest state so the BroadcastChannel onmessage
  // handler can reply to "request_state" without re-binding on every
  // state update.
  const latestStateRef = useRef<GameState>(state);
  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  // Broadcast the current state on every change so any open pop-out stays
  // live. No-op when no channel is bound (BroadcastChannel unsupported, or
  // no current story).
  useEffect(() => {
    broadcastChannelRef.current?.postMessage({ type: "state", state });
  }, [state]);

  // Poll the popped-out window's `.closed` property every 2s and drop the
  // ref when it goes true. The effect's tick dependency lets a new pop-out
  // restart the interval cleanly. The interval is a no-op while no pop-out
  // is referenced; we still mount it so the cleanup path is symmetric.
  useEffect(() => {
    if (!popoutRef.current) return;
    const tick = setInterval(() => {
      if (popoutRef.current && popoutRef.current.closed) {
        popoutRef.current = null;
        // Force a re-render so handleOpenMap re-evaluates the popout-alive
        // branch on the next click.
        setPopoutTick((n) => n + 1);
      }
    }, 2000);
    return () => clearInterval(tick);
  }, [popoutTick]);

  // Open the pop-out for the current story. Same-origin URL so localStorage
  // tokens travel; the BroadcastChannel name embeds the storyId so two
  // pop-outs for different stories don't cross-talk.
  const handleOpenMapPopout = useCallback(() => {
    if (!currentStory?.id) return;
    // If a pop-out is already alive, focus it rather than spawning a duplicate.
    if (popoutRef.current && !popoutRef.current.closed) {
      try {
        popoutRef.current.focus();
      } catch {
        // ignore — some browsers refuse cross-window focus()
      }
      setShowMap(false);
      return;
    }
    const url = `${window.location.origin}/?map=${encodeURIComponent(currentStory.id)}`;
    const win = window.open(
      url,
      `ember-map-${currentStory.id}`,
      "popup,width=900,height=900,resizable=yes,scrollbars=no",
    );
    if (!win) {
      // Pop-up blocker. Surface a toast — the in-game modal stays open so
      // the user can still see the map and choose a different course.
      pushToast({
        text: t("game:map.popoutBlocked"),
        variant: "warn",
      });
      return;
    }
    try {
      win.focus();
    } catch {
      // ignore
    }
    popoutRef.current = win;
    setPopoutTick((n) => n + 1);
    // Close the in-app modal — the pop-out is now the active map.
    setShowMap(false);
  }, [currentStory?.id, pushToast, t]);

  // Replaces the legacy `setShowMap(true)` paths that open the map. If a
  // pop-out is alive we focus that instead of opening the in-app modal.
  // (The `map` command also routes here.)
  const handleOpenMap = useCallback(() => {
    if (popoutRef.current && !popoutRef.current.closed) {
      try {
        popoutRef.current.focus();
      } catch {
        // ignore
      }
      return;
    }
    setShowMap(true);
  }, []);

  // ── Manage subscription (user-chip menu) ──────────────────────────────
  const handleManageSubscription = useCallback(async () => {
    try {
      const { url } = await openBillingPortal();
      if (!url) throw new Error("No portal URL");
      window.location.href = url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      pushToast(t("common:toasts.couldntOpenPortal", { message: shortErr(msg) }));
    }
  }, [pushToast, t]);

  const handleBuyTurns = useCallback(() => {
    // Manual open from the user-chip or pill — always shows the
    // marketing variant, not the story_locked variant.
    setPaywallReason("manual");
    setShowPaywall(true);
  }, []);

  // ── Story refresh helper ─────────────────────────────────────────────
  // Re-fetches the story list so the picker rows reflect the latest
  // lastPlayedAt + status after a switch / create / delete. Best-effort.
  const refreshStories = useCallback(async () => {
    try {
      const list = await listStories();
      setAllStories(list.stories);
      return list;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[stories] list failed:", msg);
      return null;
    }
  }, []);

  // ── Open the in-game story picker (from user-chip menu) ──────────────
  const handleOpenStoryPicker = useCallback(() => {
    // Fire-and-forget refresh so the picker's lastPlayedAt rows are fresh.
    void refreshStories();
    setShowStoryPicker(true);
  }, [refreshStories]);

  // ── Open the in-game bookshelf (from user-chip menu) ────────────────
  const handleOpenBookshelf = useCallback(() => {
    // Refresh story list so the bookshelf has current lastPlayedAt and
    // chapter counts.
    void refreshStories();
    setShowBookshelf(true);
    setBookReader(null);
  }, [refreshStories]);

  // ── Open Referrals dashboard (from user-chip menu / bonus toast) ────
  const handleOpenReferrals = useCallback(() => {
    setShowReferrals(true);
  }, []);

  // ── Finale panel actions (inline end-of-story completion surface) ─────
  // "Read your book" — open this story's BookReader at chapter 1 (the same
  // reader the Bookshelf row's "Read" opens).
  const handleFinaleReadBook = useCallback(() => {
    if (!currentStory) return;
    setBookReader({ storyId: currentStory.id, chapterNumber: 1 });
  }, [currentStory]);

  // "Continue from a different point" — the author-private fork flow. This
  // NEVER publishes anything. We open the in-app BookReader for the author's
  // OWN story in branch-enabled mode; each chapter there exposes a "Continue
  // the story from here" button that calls POST /api/stories/:id/fork (owner-
  // only, no slug, no referral) and switches the player into the new private
  // continuation via the existing activate-story path.
  const handleFinaleContinueFromPoint = useCallback(() => {
    if (!currentStory) return;
    setBookReader({
      storyId: currentStory.id,
      chapterNumber: 1,
      branchEnabled: true,
    });
  }, [currentStory]);

  // "Share your story" — open the existing ShareModal (copy link / native
  // share / publish). The Finale's Facebook button (no shareUrl prop in 2a)
  // also routes here to publish-first.
  const handleFinaleShare = useCallback(() => {
    if (!currentStory) return;
    setFinaleShareStoryId(currentStory.id);
  }, [currentStory]);

  // "Restart" — reuse the existing typed-`restart` path verbatim.
  const handleFinaleRestart = useCallback(() => {
    void handleSubmit(undefined, "restart");
  }, []);

  // "New story" — open the story switcher / picker (same as the user-chip
  // "Switch story" action).
  const handleFinaleNewStory = useCallback(() => {
    handleOpenStoryPicker();
  }, [handleOpenStoryPicker]);

  // ── Open Account settings (delete-my-account flow) ──────────────────
  const handleOpenAccountSettings = useCallback(() => {
    setShowAccountSettings(true);
  }, []);

  // ── Open Player-identity edit overlay ───────────────────────────────
  const handleOpenPlayerIdentity = useCallback(() => {
    setShowPlayerIdentity(true);
  }, []);

  // ── Cancel pending deletion from the persistent banner ──────────────
  const handleCancelPendingDeletion = useCallback(async () => {
    try {
      await cancelAccountDeletionApi();
      setPendingDeletionAt(null);
      pushToast(t("common:toasts.deletionCancelled"), 5000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      pushToast(t("common:toasts.couldntCancelDeletion", { message: msg }));
    }
  }, [pushToast, t]);

  // ── Switch to a different story ──────────────────────────────────────
  // Identity (state.player) is account-level; carry it across the switch
  // and persist into the destination story so reload preserves it. Then
  // flip the server's active pointer so /api/turn lands in the right slot.
  const handleChooseStory = useCallback(
    async (newId: string) => {
      // No-op if the user picked the active one — close the picker.
      if (currentStory && currentStory.id === newId) {
        setShowStoryPicker(false);
        return;
      }
      try {
        const { story } = await getStory(newId);
        const migrated = migrateState(story.state, story.scenarioId);
        const switched: GameState = { ...migrated, player: state.player };
        // Push identity into the destination story before activating, so
        // the next reload of that story preserves the carried player.
        // Existing stories are always writable by their owner under the
        // current scenario-lock rules (STORIES_CONTRACT §1) — the PUT
        // will succeed regardless of subscription state.
        await putStory(newId, switched);
        await activateStory(newId);
        setCurrentStory({
          id: story.id,
          scenarioId: story.scenarioId,
          status: story.status,
          name: story.name,
          requiresSubscriptionToWrite: story.requiresSubscriptionToWrite,
        });
        setState(switched);
        // Switching to an existing story is NOT a new-story flow — clear
        // any stale awaitingName flag defensively (it shouldn't be set,
        // but switching mid-NamePrompt would otherwise leave us stuck).
        setAwaitingName(false);
        if (switched.player.setupComplete) {
          setLastKnownIdentity({
            gender: switched.player.gender,
            drawnTo: switched.player.drawnTo,
            age: switched.player.age,
            drawnToAgeMin: switched.player.drawnToAgeMin,
            drawnToAgeMax: switched.player.drawnToAgeMax,
          });
        }
        // Sticky guidance preference — same logic as boot hydration.
        if (
          switched.guidance &&
          typeof switched.guidance.level === "number" &&
          Number.isInteger(switched.guidance.level)
        ) {
          setLastKnownGuidance(switched.guidance.level);
        }
        setBootStatus("loaded saved game");
        setShowStoryPicker(false);
        setShowMap(false);
        setShowDebug(false);
        // Refresh the sidebar list so lastPlayedAt is current next time.
        void refreshStories();
        pushToast(
          t("common:toasts.switchedTo", {
            name: story.name || t("bookshelf:bookReader.untitled", { defaultValue: "Untitled" }),
          }),
        );
      } catch (err) {
        const msg =
          err instanceof StoriesApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        console.warn("[stories] switch failed:", msg);
        pushToast(t("common:toasts.switchFailed", { message: shortErr(msg) }));
      }
    },
    [currentStory, refreshStories, state.player, pushToast, t],
  );

  // ── Phase 3b "next book": spawn Book N+1 and switch into it ──────────
  //
  // Replaces the 2a "coming soon" stub. The Finale card hands us the chosen
  // option's id; we look up the FULL SequelOption (App holds the whole object,
  // the card only renders a subset), POST it to /api/stories/:id/sequel
  // (owner-only, finished-only on the server), and on success switch the
  // player into the freshly-spawned book via the SAME activate path the fork
  // uses (handleChooseStory). That dismisses the Finale implicitly because the
  // new active story has gameOver = null. Busy state on the chosen card; toast
  // on error. Declared AFTER handleChooseStory so the dep closure is valid.
  const handleBeginSequel = useCallback(
    (optionId: string) => {
      if (!currentStory) return;
      const option = finaleSequelOptions.find((o) => o.id === optionId);
      if (!option) {
        pushToast(t("common:toasts.switchFailed", { message: "option not found" }));
        return;
      }
      if (beginningSequelId) return; // a spawn is already in flight
      setBeginningSequelId(optionId);
      void (async () => {
        try {
          const { newStoryId } = await beginSequel(currentStory.id, option);
          // Switch into the new book — same activate path as the fork flow.
          // This sets the active story (gameOver null) which closes the Finale.
          await handleChooseStory(newStoryId);
          // Clear the finished story's slate from hand so a stale Finale (if
          // any) doesn't keep showing the old cards.
          setFinaleSequelOptions([]);
          setSequelOptionsStoryId(null);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn("[stories] beginSequel failed:", msg);
          pushToast(t("common:toasts.switchFailed", { message: shortErr(msg) }));
        } finally {
          setBeginningSequelId(null);
        }
      })();
    },
    [
      currentStory,
      finaleSequelOptions,
      beginningSequelId,
      handleChooseStory,
      pushToast,
      t,
    ],
  );

  // ── Phase 3b: hydrate the slate for a REOPENED finale ────────────────
  //
  // When a previously-finished story is showing its Finale (gameOver set on a
  // loaded save) but we don't hold its slate in hand (the winning-turn
  // response is long gone), fetch the persisted options once via
  // getSequelOptions. Guarded by sequelOptionsStoryId so we fetch at most once
  // per finished story; the winning-turn capture sets sequelOptionsStoryId so
  // this effect won't re-fetch over a fresh in-hand slate.
  useEffect(() => {
    if (!state.gameOver || state.gameOver !== "win") return;
    if (!currentStory) return;
    if (sequelOptionsStoryId === currentStory.id) return; // already have it
    const storyId = currentStory.id;
    setSequelOptionsStoryId(storyId);
    void (async () => {
      try {
        const opts = await getSequelOptions(storyId);
        // Guard against a race where the active story changed mid-fetch.
        setFinaleSequelOptions((prev) =>
          sequelOptionsStoryId === storyId || prev.length === 0 ? opts : prev,
        );
      } catch (err) {
        // No options is the graceful default — a fetch miss just shows none.
        console.warn(
          "[stories] getSequelOptions failed:",
          err instanceof Error ? err.message : String(err),
        );
      }
    })();
    // sequelOptionsStoryId intentionally read inside; we set it synchronously
    // above to debounce. currentStory.id + gameOver are the real triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.gameOver, currentStory?.id]);

  // ── Phase 2 fork-from-public-reader handoff ──────────────────────────
  //
  // Two URL patterns to handle on post-auth landing:
  //
  //   ?fork=<slug>&chapter=<N>
  //     Means: an unauthed reader on story.* clicked "Continue from chapter
  //     N" → they were routed through signin → we land here authed. POST
  //     the fork now, then activate the new story.
  //
  //   ?activeStoryId=<id>&forkFromSlug=<slug>&forkFromChapter=<N>
  //     Means: an authed reader on story.* completed the fork themselves
  //     and just wants the player surface to land on the new story. We
  //     activate + toast.
  //
  // Either way, we strip the params from the URL so a refresh doesn't
  // re-run the action. Placed AFTER handleChooseStory's declaration so
  // the effect's closure-capture doesn't TDZ-trip.
  const forkHandledRef = useRef(false);
  useEffect(() => {
    if (forkHandledRef.current) return;
    if (!saveLoaded) return; // wait for boot to settle
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const forkSlug = params.get("fork");
    const forkChapterRaw = params.get("chapter");
    const activeStoryIdParam = params.get("activeStoryId");
    const forkFromSlugParam = params.get("forkFromSlug");
    const forkFromChapterRaw = params.get("forkFromChapter");

    const stripParams = (keys: string[]): void => {
      try {
        const url = new URL(window.location.href);
        for (const k of keys) url.searchParams.delete(k);
        const search = url.searchParams.toString();
        const next = url.pathname + (search ? `?${search}` : "") + url.hash;
        window.history.replaceState(null, "", next);
      } catch {
        // ignore
      }
    };

    // Case 1: post-auth fork — execute the fork now.
    if (forkSlug && forkChapterRaw) {
      const chapterNum = Number(forkChapterRaw);
      if (!Number.isFinite(chapterNum) || chapterNum <= 0) {
        stripParams(["fork", "chapter"]);
        return;
      }
      forkHandledRef.current = true;
      stripParams(["fork", "chapter"]);
      void (async () => {
        try {
          const res = await forkPublicBook(forkSlug, Math.trunc(chapterNum));
          await handleChooseStory(res.newStoryId);
          pushToast({
            variant: "success",
            text: t("common:toasts.forkSuccess", { chapter: chapterNum }),
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn("[fork] post-auth fork failed:", msg);
          pushToast(t("common:toasts.forkFailed", { message: shortErr(msg) }));
        }
      })();
      return;
    }

    // Case 2: already-forked, just activate + toast.
    if (activeStoryIdParam) {
      forkHandledRef.current = true;
      const slug = forkFromSlugParam;
      const chapterNum = forkFromChapterRaw ? Number(forkFromChapterRaw) : null;
      stripParams([
        "activeStoryId",
        "forkFromSlug",
        "forkFromChapter",
      ]);
      void (async () => {
        try {
          await handleChooseStory(activeStoryIdParam);
          if (slug && chapterNum && Number.isFinite(chapterNum)) {
            pushToast({
              variant: "success",
              text: t("common:toasts.forkSuccess", { chapter: chapterNum }),
            });
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn("[fork] post-fork activate failed:", msg);
          pushToast(t("common:toasts.forkActivateFailed", { message: shortErr(msg) }));
        }
      })();
    }
  }, [saveLoaded, handleChooseStory, pushToast, t]);

  // ── Start-new flow: from StoryPicker → open ScenarioPicker ───────────
  const handleStartNewStory = useCallback(() => {
    if (scenarioCatalogue.length === 0) {
      // First open — fetch the catalogue. The picker shows a "Loading…"
      // state while we wait. We keep showStoryPicker true so the user has
      // somewhere to go if the fetch fails (and the cancel X works).
      void (async () => {
        try {
          const cat = await listScenarios();
          setScenarioCatalogue(cat.scenarios);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          console.warn("[scenarios] load failed:", msg);
          pushToast(t("common:toasts.scenariosFailed", { message: shortErr(msg) }));
        }
      })();
    }
    setShowScenarioPicker(true);
  }, [scenarioCatalogue.length, pushToast, t]);

  // ── Pick a scenario → create the story → hydrate ─────────────────────
  // The story is created with a placeholder name (server default "Untitled");
  // the user names it later via the post-CharacterSetup NamePrompt. Setting
  // `awaitingName` true tells the render branch to show that prompt once
  // CharacterSetup (if required) is complete.
  const handlePickScenario = useCallback(
    async (scenarioId: string, language?: string) => {
      try {
        // `language` is set ONLY when the user opened the ScenarioPicker
        // "More options" expander and picked an override; otherwise it's
        // undefined and the server uses the account default. See
        // STORIES_CONTRACT and ARCHITECTURE.md "Localisation".
        const { story } = await createStory(scenarioId, undefined, language);
        const migrated = migrateState(story.state, story.scenarioId);
        setCurrentStory({
          id: story.id,
          scenarioId: story.scenarioId,
          status: story.status,
          name: story.name,
          requiresSubscriptionToWrite: story.requiresSubscriptionToWrite,
        });
        setState(migrated);
        setAwaitingName(true);
        setBootStatus("fresh start");
        await refreshStories();
        setShowScenarioPicker(false);
        setShowStoryPicker(false);
        setShowMap(false);
        setShowDebug(false);
      } catch (err) {
        if (err instanceof StoryCapReachedError) {
          pushToast(t("common:toasts.storyCapReached"));
          setPaywallReason("story_cap");
          setShowPaywall(true);
          return;
        }
        if (err instanceof SubscriptionRequiredError) {
          // Forever-default lock — user picked a scenario other than their
          // forever_default. Open the Paywall with the story_locked reason.
          pushToast(t("common:toasts.subscribeToPlayWorld"));
          setPaywallReason("story_locked");
          setShowPaywall(true);
          return;
        }
        if (err instanceof ScenarioLockedError) {
          // Legacy free-tier scenario lock (older server). Surface the
          // same paywall flow.
          pushToast(t("common:toasts.subscribeToPlayWorld"));
          setPaywallReason("story_locked");
          setShowPaywall(true);
          return;
        }
        const msg =
          err instanceof StoriesApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        console.warn("[stories] create failed:", msg);
        pushToast(t("common:toasts.createFailed", { message: shortErr(msg) }));
      }
    },
    [refreshStories, pushToast, t],
  );

  // ── Delete a story (StoryPicker confirms inline) ─────────────────────
  // After delete, refresh the list. If the deleted row was the active
  // story, the server has auto-activated the next-most-recent (or none).
  // Either re-hydrate from the new active id or, if there's nothing left,
  // force the ScenarioPicker so the user can begin a new one.
  const handleDeleteStory = useCallback(
    async (id: string) => {
      try {
        await deleteStory(id);
        const list = await refreshStories();
        const wasCurrent = currentStory?.id === id;
        if (!wasCurrent) {
          pushToast(t("common:toasts.storyDeleted"));
          return;
        }
        // We just deleted the story we were playing. Pick up whatever the
        // server now considers active. Clear the new-story-naming flag if
        // it was somehow still on (deleting the story we were about to
        // name shouldn't leave us stuck in the NamePrompt branch).
        setAwaitingName(false);
        if (!list || list.stories.length === 0) {
          setCurrentStory(null);
          setState(makeInitialState());
          setShowStoryPicker(false);
          setShowScenarioPicker(true);
          if (scenarioCatalogue.length === 0) {
            try {
              const cat = await listScenarios();
              setScenarioCatalogue(cat.scenarios);
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err);
              console.warn("[scenarios] load failed:", msg);
            }
          }
          pushToast(t("common:toasts.storyDeleted"));
          return;
        }
        // Server picks next-most-recent active as the new pointer. If
        // activeStoryId is null (e.g. only finished stories remain), drop
        // the user back at the picker.
        const newActiveId = list.activeStoryId;
        if (!newActiveId) {
          setCurrentStory(null);
          setState(makeInitialState());
          setShowStoryPicker(true);
          pushToast(t("common:toasts.storyDeleted"));
          return;
        }
        const { story } = await getStory(newActiveId);
        setCurrentStory({
          id: story.id,
          scenarioId: story.scenarioId,
          status: story.status,
          name: story.name,
          requiresSubscriptionToWrite: story.requiresSubscriptionToWrite,
        });
        setState(migrateState(story.state, story.scenarioId));
        setShowStoryPicker(false);
        setShowMap(false);
        setShowDebug(false);
        pushToast(t("common:toasts.storyDeleted"));
      } catch (err) {
        const msg =
          err instanceof StoriesApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : String(err);
        console.warn("[stories] delete failed:", msg);
        pushToast(t("common:toasts.deleteFailed", { message: shortErr(msg) }));
      }
    },
    [currentStory, refreshStories, scenarioCatalogue.length, pushToast, t],
  );

  // ── Guidance change from the user-chip submenu ────────────────────────
  // Mirror of the in-game `guidance <mode>` command, but reachable via the
  // user-chip dropdown. Persists immediately — the level rides in the
  // JSONB save like any other state field.
  const handleGuidanceChange = useCallback(
    (level: number) => {
      setState((s) => {
        const next = setGuidanceLevel(s, level);
        const finalState: GameState = {
          ...next,
          log: [
            ...next.log,
            {
              kind: "system",
              text: t("game:guidance.setTo", {
                label: t(`game:guidance.${guidanceLabel(level)}`),
              }),
            },
          ],
        };
        void persist(finalState);
        return finalState;
      });
      // Sticky preference: a mid-story guidance change also updates the
      // sticky pref so the next new story spawns at the same level.
      setLastKnownGuidance(level);
    },
    [persist, t]
  );

  // ── Spice override (admin-only) from the user-chip submenu ────────────
  // Real players never trigger this — their spice adapts silently via the
  // model emitting setSpice. Admins use this to land at a specific spice
  // level for testing without having to roleplay engagement to ramp it up.
  //
  // The write goes through the dedicated POST /api/admin/spice endpoint
  // which validates users.isAdmin server-side. UI gating on user.isAdmin
  // (below, where the submenu is rendered) is just the surface; the
  // server check is the belt to those braces. On 204 success we
  // optimistically apply locally so the UI updates immediately; the
  // server-side row is the authoritative copy.
  const handleSpiceChange = useCallback(
    async (level: number) => {
      if (!currentStory) return;
      try {
        await adminSetSpice(currentStory.id, level);
      } catch (err) {
        // adminSetSpice swallows 400 + 204; only 5xx / network errors get
        // here. Don't surface — the slider is admin-only and a hiccup
        // can be diagnosed from the network panel.
        console.warn("[admin:spice] request failed:", err);
        return;
      }
      setState((s) => {
        const next = setSpiceLevel(s, level);
        // Persist locally too so a refresh before the next /api/turn
        // doesn't undo the change from the in-memory React state. The
        // server row was already updated by /api/admin/spice; this
        // PUT is belt-and-braces.
        void persist(next);
        return next;
      });
    },
    [currentStory, persist],
  );

  // ── Turn handler ───────────────────────────────────────────────────────
  // The optional `override` argument lets the Retry button on a failed
  // error LogEntry resubmit its `pendingInput` without going through the
  // input <textarea>. When provided we use it directly; otherwise we read
  // the current `input` state. The `setInput("")` calls further down still
  // run unconditionally — that's a no-op on the override path (input was
  // never typed there) and the correct clear on the normal path.
  async function handleSubmit(e?: FormEvent, override?: string) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const trimmed = (override ?? input).trim();
    if (!trimmed || busy) return;

    const lower = trimmed.toLowerCase();
    // Resolve localized meta-commands back to their canonical English form
    // (Phase D i18n). The model narrates in the player's language and may
    // suggest commands by their localized name (e.g. "mapa", "ヘルプ");
    // the engine accepts both. English forms remain universal.
    //
    // `meta` is the canonical resolution of the WHOLE input. For commands
    // that take a sub-argument (only `guidance` today) we also resolve the
    // first whitespace-split token so e.g. "guía read" still works.
    const meta = resolveMetaCommand(lower);
    const firstToken = lower.split(/\s+/, 1)[0] ?? "";
    const metaFirst = resolveMetaCommand(firstToken);

    // Local commands — bypass the API for speed. None of these should re-save
    // server state (help/map/debug are pure UI; restart does save).
    if (meta === "help") {
      setState((s) => ({
        ...s,
        log: [
          ...s.log,
          { kind: "input", text: trimmed },
          {
            kind: "system",
            text: t("game:helpText"),
          },
        ],
      }));
      setInput("");
      return;
    }
    if (meta === "map") {
      // If a pop-out is alive, focus it and don't toggle the in-app modal.
      // Otherwise toggle: closed → handleOpenMap (which still defers to a
      // live pop-out as a guard), open → close.
      if (popoutRef.current && !popoutRef.current.closed) {
        try {
          popoutRef.current.focus();
        } catch {
          // ignore
        }
      } else if (showMap) {
        setShowMap(false);
      } else {
        handleOpenMap();
      }
      setInput("");
      return;
    }
    if (meta === "debug") {
      setShowDebug((d) => !d);
      setInput("");
      return;
    }
    if (lower === "signout" || lower === "sign out" || lower === "logout") {
      onSignOut();
      return;
    }
    if (meta === "guidance" || metaFirst === "guidance") {
      // Local-only: adjust the per-story adaptive-narration slider. The new
      // level is persisted via the regular save flow (state.guidance lives
      // in the JSONB blob — no new endpoint needed).
      //
      // Sub-arguments cover all 5 named modes (story / guided / classic /
      // sparse / expert) plus the legacy aliases `read` (→ story) and
      // `hard` (→ expert) so muscle-memory players don't break. Localised
      // sub-arg forms can be added later via a small alias table.
      const arg = meta === "guidance"
        ? ""
        : lower.slice(firstToken.length).trim();
      const helpLine = t("game:guidance.currentlyHelp", {
        label: t(`game:guidance.${guidanceLabel(state.guidance?.level ?? 0)}`),
      });
      if (arg === "") {
        setState((s) => ({
          ...s,
          log: [
            ...s.log,
            { kind: "input", text: trimmed },
            { kind: "system", text: helpLine },
          ],
        }));
        setInput("");
        return;
      }
      let nextLevel: number | null = null;
      if (arg === "story" || arg === "story mode" || arg === "read" || arg === "read mode") {
        nextLevel = 2;
      } else if (arg === "guided") {
        nextLevel = 1;
      } else if (arg === "classic") {
        nextLevel = 0;
      } else if (arg === "sparse") {
        nextLevel = -1;
      } else if (arg === "expert" || arg === "expert mode" || arg === "hard" || arg === "hard mode") {
        nextLevel = -2;
      }

      if (nextLevel === null) {
        setState((s) => ({
          ...s,
          log: [
            ...s.log,
            { kind: "input", text: trimmed },
            { kind: "system", text: helpLine },
          ],
        }));
        setInput("");
        return;
      }

      const withGuidance = setGuidanceLevel(state, nextLevel);
      const finalState: GameState = {
        ...withGuidance,
        log: [
          ...withGuidance.log,
          { kind: "input", text: trimmed },
          {
            kind: "system",
            text: t("game:guidance.setTo", {
              label: t(`game:guidance.${guidanceLabel(nextLevel)}`),
            }),
          },
        ],
      };
      setState(finalState);
      setInput("");
      void persist(finalState);
      // Sticky preference: the in-game `guidance <mode>` command also
      // updates the sticky pref so the next new story spawns here.
      setLastKnownGuidance(nextLevel);
      return;
    }
    if (meta === "restart") {
      // Restart THIS story (preserving player identity); does NOT create a
      // new story or change the scenario. The server rebuilds fresh state
      // from the scenario's openingRoom + openingItem and copies the
      // player identity forward — the response is the new full story.
      if (!currentStory) return; // defensive — never reachable from game UI.
      // Restart is always allowed on an owned story under the current
      // scenario-lock rules (STORIES_CONTRACT §1). The server only rejects
      // restart for a finalised story (409 story_finished) — handled below.
      setInput("");
      void restartStory(currentStory.id)
        .then(({ story }) => {
          setState(migrateState(story.state, story.scenarioId));
          setLastError(null);
          pushToast(t("common:toasts.storyRestarted"));
        })
        .catch((err) => {
          if (err instanceof StoryFinishedError) {
            pushToast(t("common:toasts.storyFinalised"));
            setShowStoryPicker(true);
            return;
          }
          const msg =
            err instanceof StoriesApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : String(err);
          console.warn("[stories] restart failed:", msg);
          pushToast(t("common:toasts.restartFailed", { message: shortErr(msg) }));
        });
      return;
    }

    // Existing stories are always playable by their owner under the
    // current scenario-lock rules (STORIES_CONTRACT §1). Turn balance is
    // the only write-gate; that 402 surfaces as OutOfTurnsError further
    // down. The previous forever-default lock short-circuit has been
    // removed — `/api/turn` no longer rejects on scenario lock.

    setInput("");
    setBusy(true);
    // Roll a fresh "thinking" phrase for this turn. Held in state so it stays
    // stable through the whole wait (no flicker on re-render) but re-rolls for
    // each new turn. Scenario-flavoured pool + generic pool, merged.
    setThinkingPhrase(pickThinkingPhrase(t, currentStory?.scenarioId));
    setLastError(null);
    // Clear any in-flight narration animation flag — the previous turn's
    // narration becomes a static historical entry the moment the next turn
    // begins. Without this, the optimistic-update gap (turnCount bumps, new
    // narration not yet present) would briefly re-animate the old entry.
    setFreshNarrationIdx(null);
    // Clear any in-flight multi-room movement path — same reasoning. A new
    // turn starts with no path; we'll set one below if the response carries
    // and validates a fresh one.
    setPendingMovementPath(null);

    // Capture the player's room AT THE START OF THE TURN — used to validate
    // movementPath[0] against the pre-turn position. We read it BEFORE
    // applyDelta runs because applyDelta may emit moveToRoom changes that
    // shift currentRoom.
    const preTurnRoomId = state.currentRoom;

    // Mobile autoplay unlock: the AudioContext stays "suspended" until a
    // user gesture. The input-submit handler is a guaranteed gesture, so
    // we resume here on every turn (idempotent when already running).
    // First turn doubles as the unlock.
    try {
      const ctx = Howler.ctx;
      if (ctx && ctx.state === "suspended") void ctx.resume();
    } catch {
      // ignore — non-Web-Audio environments
    }

    const withInput: GameState = {
      ...state,
      log: [...state.log, { kind: "input", text: trimmed }],
      turnCount: state.turnCount + 1,
    };
    setState(withInput);

    try {
      const condensed = condenseStateForModel(withInput);
      const result = await askEngine(
        condensed,
        trimmed,
        lastSeenChapterAt.current,
      );
      // Bookshelf wiring: surface any chapters the server has generated
      // since our last turn. Push a toast per entry — see notifyChapter().
      // Bump the cursor BEFORE running the side effects so re-entrant
      // pushes (rare) read the fresh timestamp.
      const fresh = result.newChaptersSinceLastTurn;
      if (Array.isArray(fresh) && fresh.length > 0) {
        lastSeenChapterAt.current = new Date().toISOString();
        for (const c of fresh) {
          notifyChapter(c);
          // For backfill batches, the server sets chapterCount on the
          // first entry and we toast once for the batch. Bail out so we
          // don't fire one toast per entry on top.
          if (c.isBackfill && typeof c.chapterCount === "number") break;
        }
      }
      // Phase 3 — referral bonus toast pipeline. Lifts the per-user Redis
      // queue the server populated for any deposits since the last turn.
      const bonuses = result.newReferralBonusesSinceLastTurn;
      if (Array.isArray(bonuses) && bonuses.length > 0) {
        notifyReferralBonuses(bonuses);
      }
      const afterDelta = applyDelta(withInput, result.stateDelta, {
        scenarioId: currentStory?.scenarioId ?? null,
        storyId: currentStory?.id ?? null,
        worldGen: getWorldGen(currentStory?.scenarioId ?? null),
      });

      // Position C — multi-room transit. The model may emit a movementPath
      // when narration takes the player through more than one room in a
      // single turn (chained input, NPC leads the player down a corridor,
      // narrative elision). We validate against the post-delta state and
      // hand the result to the map for step-animation. Invalid paths log a
      // warn and fall through to today's single-hop CSS-transition.
      const rawPath = result.stateDelta?.movementPath;
      if (rawPath !== undefined) {
        let reason: string | null = null;
        if (!Array.isArray(rawPath)) {
          reason = "not an array";
        } else if (rawPath.length < 2) {
          reason = `length ${rawPath.length} < 2`;
        } else if (!rawPath.every((id) => typeof id === "string")) {
          reason = "non-string id in path";
        } else if (rawPath[0] !== preTurnRoomId) {
          reason = `first id ${JSON.stringify(rawPath[0])} != preTurnRoomId ${JSON.stringify(preTurnRoomId)}`;
        } else if (rawPath[rawPath.length - 1] !== afterDelta.currentRoom) {
          reason = `last id ${JSON.stringify(rawPath[rawPath.length - 1])} != afterDelta.currentRoom ${JSON.stringify(afterDelta.currentRoom)}`;
        } else {
          for (let i = 0; i < rawPath.length; i++) {
            const rid = rawPath[i];
            if (!afterDelta.rooms[rid]) {
              reason = `path[${i}] ${JSON.stringify(rid)} missing from afterDelta.rooms`;
              break;
            }
          }
          if (!reason) {
            for (let i = 0; i < rawPath.length - 1; i++) {
              const a = rawPath[i];
              const b = rawPath[i + 1];
              const exits = afterDelta.rooms[a]?.exits ?? {};
              let connected = false;
              for (const dir of Object.keys(exits)) {
                if (exits[dir] === b) {
                  connected = true;
                  break;
                }
              }
              if (!connected) {
                reason = `path[${i}]→path[${i + 1}] (${JSON.stringify(a)}→${JSON.stringify(b)}) not connected by any exit`;
                break;
              }
            }
          }
        }
        if (reason) {
          // eslint-disable-next-line no-console
          console.warn(
            "[App] invalid movementPath rejected: " + reason,
            rawPath,
          );
        } else {
          setPendingMovementPath(rawPath as string[]);
        }
      }

      // First-turn agency cue — appended exactly once per story, on the
      // first successful turn (turnCount transitions from 0→1). Players
      // coming from classic-IF parser games discover here that free prose
      // is welcome ("I crouch slowly to keep her from hearing me" works
      // just as well as "hide"). Once the turn lands, the cue lives in
      // state.log forever — it persists with the save, so a reload won't
      // re-show it. Gating strictly on turnCount === 1 (post-applyDelta)
      // means: a turn that 402s and gets rewound (OutOfTurnsError path)
      // still gets the cue once the player buys turns and retries; a
      // turn that errors-with-retry on its first try is the only edge
      // case that misses the cue (turnCount stayed bumped through the
      // error). Acceptable — that path is rare and the help command
      // surfaces the same examples on demand.
      const isFirstSuccessfulTurn = afterDelta.turnCount === 1;
      const finalState: GameState = {
        ...afterDelta,
        log: [
          ...afterDelta.log,
          { kind: "narration", text: result.narration || "(silence)" },
          ...(isFirstSuccessfulTurn
            ? [{ kind: "system" as const, text: t("game:agencyTip") }]
            : []),
        ],
        gameOver: result.deathOrWin || null,
      };
      setState(finalState);
      // Phase 3b — capture the "next book" slate the server generated for a
      // WINNING turn so the Finale shows the cards immediately, no follow-up
      // fetch. Setting sequelOptionsStoryId here ALSO suppresses the reopened-
      // finale effect from re-fetching over this fresh in-hand slate. A
      // non-winning turn clears any stale slate.
      if (result.deathOrWin === "win" && currentStory) {
        setFinaleSequelOptions(result.sequelOptions ?? []);
        setSequelOptionsStoryId(currentStory.id);
      }
      // Mark the just-appended narration as the fresh one — its index is
      // the last entry. Triggers the word-by-word reveal exactly once.
      setFreshNarrationIdx(finalState.log.length - 1);
      // Phase A audio: lift the model's one-shot signal into state so
      // useAudio fires it exactly once. We encode `${tag}:${turn}` so a
      // model emitting the same tag on two consecutive turns still
      // triggers a new play (the nonce differs).
      const sfxTag = result.stateDelta?.playSfx;
      if (typeof sfxTag === "string" && sfxTag.length > 0) {
        setLastSfx(`${sfxTag}:${finalState.turnCount}`);
      }
      void persist(finalState);
      // Server has just decremented the balance — pull the fresh value so
      // the header pill tracks. Don't await; the UI shouldn't block on this.
      void refreshBilling();

      // Story rename — the model may emit setStoryName ONCE the story takes
      // shape (after the first puzzle anchors, the first companion appears,
      // or the player's working objective becomes legible). It's an App-layer
      // effect, not a state mutation, so we handle it here rather than in
      // applyDelta. Validate + trim + clamp at 80 chars, then PATCH the
      // server. On success mirror locally and toast; on failure warn-log
      // silently — the model can re-emit next turn if it wants to.
      const newStoryName = result.stateDelta?.setStoryName;
      if (
        typeof newStoryName === "string" &&
        newStoryName.trim().length > 0 &&
        currentStory &&
        newStoryName.trim() !== currentStory.name
      ) {
        const clamped = newStoryName.trim().slice(0, 80);
        void renameStory(currentStory.id, clamped)
          .then(({ story }) => {
            setCurrentStory((cs) => (cs ? { ...cs, name: story.name } : cs));
            setAllStories((all) =>
              all.map((s) => (s.id === story.id ? story : s)),
            );
            pushToast(t("common:toasts.storyHasName", { name: story.name }));
          })
          .catch((err) => {
            console.warn("[setStoryName] rename failed:", err);
            // Don't bother the player; the model can re-emit next turn.
          });
      }
    } catch (err: unknown) {
      // 402 → open Paywall, don't write to the log. The modal IS the messaging.
      if (err instanceof OutOfTurnsError) {
        // Rewind the input-line + turnCount we optimistically added — the
        // turn never executed (server returned 402 before Anthropic was hit).
        setState((s) => ({
          ...s,
          log: s.log.slice(0, -1),
          turnCount: Math.max(0, s.turnCount - 1),
        }));
        // Force the displayed balance to 0 so the pill matches the modal copy.
        setBilling((b) => ({ ...b, balance: 0, loaded: true }));
        setPaywallReason("out_of_turns");
        setShowPaywall(true);
        return;
      }
      // 503 upstream_capacity → operator-side capacity issue (typically
      // Anthropic credit exhausted). Server has already refunded the turn.
      // Keep the optimistic input entry visible (the player should always
      // see what they typed) and APPEND a retryable error entry carrying
      // the original input. The inline Retry button on the error LogLine
      // resubmits via handleSubmit(undefined, pendingInput). turnCount
      // stays bumped — a turn was attempted; the next turn (or retry)
      // bumps again.
      if (err instanceof UpstreamCapacityError) {
        setState((s) => ({
          ...s,
          log: [
            ...s.log,
            {
              kind: "error",
              text: t("common:errors.upstreamCapacity"),
              pendingInput: trimmed,
            },
          ],
        }));
        return;
      }
      console.error("Engine error:", err);
      const errMsg = err instanceof Error ? err.message : String(err);
      setLastError(errMsg);
      // Generic engine failure — same shape as the 503 path: keep the
      // optimistic input visible and append a retryable error entry. The
      // Retry button on the LogLine resubmits the carried text.
      setState((s) => ({
        ...s,
        log: [
          ...s.log,
          {
            kind: "error",
            text: t("common:errors.engineStuttered", { message: errMsg }),
            pendingInput: trimmed,
          },
        ],
      }));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  const cur = state.rooms[state.currentRoom];

  // ── Theme wrapper helper ──────────────────────────────────────────────
  // Every in-game render branch (loading screen, error screen, picker,
  // CharacterSetup, billing return, the game UI itself) is wrapped in a
  // ScenarioThemeWrapper bound to the current story's scenarioId. As the
  // user switches stories the wrapper re-paints the page in the new
  // scenario's palette.
  //
  // currentStory may be null briefly during boot — the wrapper handles
  // the null case by falling through to the styles.ts var() fallbacks
  // (Ember Hollow gothic) so we never see an unstyled flash.
  const activeScenarioId = currentStory?.scenarioId ?? null;
  // The wrapper reads state.dimension to apply per-dimension theme overlays
  // for scenarios that declare them (Veridian Bloom, The Sleeping Floor).
  // Other scenarios don't declare dimensionThemes and the value is ignored.
  const activeDimension =
    typeof state.dimension === "number" && Number.isFinite(state.dimension)
      ? state.dimension
      : 0;
  const themed = (node: React.ReactNode) => (
    <ScenarioThemeWrapper
      scenarioId={activeScenarioId}
      currentDimension={activeDimension}
    >
      {node}
    </ScenarioThemeWrapper>
  );

  // ── Boot loading screen ───────────────────────────────────────────────
  // Render until the stories fetch settles. Without this guard we'd flash
  // the empty initial state (or worse, the CharacterSetup gate) before
  // the picker / hydration kicks in.
  if (!saveLoaded) {
    return themed(<BootLoadingScreen />);
  }

  // ── Story service unreachable ─────────────────────────────────────────
  // We've heard back from /api/stories and it failed. Show the same
  // surface as a billing-return error so the user knows the game can't
  // start. Toast already fired during the boot effect.
  if (bootStatus === "save unreachable" && !currentStory) {
    return themed(<StoryServiceUnreachableScreen />);
  }

  // ── ScenarioPicker as forced gate (no current story, no other UI) ─────
  // Renders alone when the user has no stories and we're forcing them to
  // pick a scenario. Inside the game we show ScenarioPicker on top of the
  // game UI instead — see below.
  if (!currentStory && showScenarioPicker) {
    return themed(
      <ScenarioPicker
        open={true}
        scenarios={scenarioCatalogue}
        onPick={(scenarioId, language) => {
          void handlePickScenario(scenarioId, language);
        }}
        onLockedClicked={() => {
          setShowScenarioPicker(false);
          setPaywallReason("story_locked");
          setShowPaywall(true);
        }}
        onClose={() => {
          // Forced gate — closing without a pick has nowhere meaningful to
          // go. Bounce them to the StoryPicker (which will be empty and
          // re-offer "Start a new story").
          setShowScenarioPicker(false);
          setShowStoryPicker(true);
        }}
        // Forced-gate path = no stories yet. Show the first-pick warning
        // so the player understands their pick is permanent on free tier.
        firstPickWarning={
          allStories.length === 0 &&
          (billing.subscription === null ||
            billing.subscription.status === "canceled" ||
            billing.subscription.status === "unpaid")
        }
        accountLanguage={user?.language ?? null}
      />,
    );
  }

  // ── StoryPicker as forced gate (no current story, multiple stories) ───
  // Renders alone when the user has stories but no active pointer (or
  // came here via the "deleted my last active" path). When mid-game the
  // picker overlays the existing UI — see below.
  if (!currentStory && showStoryPicker) {
    return themed(
      <StoryPicker
        stories={allStories}
        activeStoryId={null}
        onChoose={(id) => {
          void handleChooseStory(id);
        }}
        onStartNew={handleStartNewStory}
        onDelete={(id) => {
          void handleDeleteStory(id);
        }}
        onRename={async (id, name) => {
          // Errors propagate to StoryPicker so the row can surface an
          // inline message and stay in edit mode. We mirror the row into
          // local state on success so the rename sticks across re-renders.
          // At the boot gate currentStory is null by construction, but the
          // guard is cheap and keeps the two call sites symmetrical.
          const { story } = await renameStory(id, name);
          setAllStories((all) =>
            all.map((s) => (s.id === id ? story : s)),
          );
          setCurrentStory((cs) =>
            cs && cs.id === id ? { ...cs, name: story.name } : cs,
          );
        }}
        // No onClose at the boot gate — the user must pick.
      />,
    );
  }

  // ── Defensive: still booting but no current story ────────────────────
  // Shouldn't normally hit (the boot effect either sets currentStory or
  // opens a picker). If it does, fall back to the loading screen rather
  // than rendering a corrupt header.
  if (!currentStory) {
    return themed(<BootLoadingScreen />);
  }

  // ── Character setup gate (one-time, before turn 1) ────────────────────
  // Show after we've hydrated the active story and only when the player
  // hasn't yet submitted the screen. Submission persists immediately so
  // refreshes don't re-prompt.
  //
  // `defaults` pre-fills the pills from the player's most-recently-seen
  // identity (any earlier story for this account). undefined on first
  // signup → blank pills, exactly as before. The user can always change
  // their selections; we don't lock them in.
  if (!state.player.setupComplete) {
    return themed(
      <CharacterSetup
        defaults={{
          gender: lastKnownIdentity?.gender ?? null,
          drawnTo: lastKnownIdentity?.drawnTo ?? null,
          age: lastKnownIdentity?.age,
          drawnToAgeMin: lastKnownIdentity?.drawnToAgeMin,
          drawnToAgeMax: lastKnownIdentity?.drawnToAgeMax,
          // First-ever-story fallback: +2 (Story Mode). Has to match the
          // engine's makeInitialState default so the slider position and
          // the persisted state.guidance.level never disagree.
          guidanceLevel: lastKnownGuidance ?? 2,
        }}
        onComplete={(identity) => {
          // applyPlayerSetup honours gender / drawnTo / age / age-range.
          // Guidance lands on state.guidance.level via setGuidanceLevel —
          // it's a separate concern (UX preference, not player identity).
          const withIdentity = applyPlayerSetup(state, identity);
          const next = setGuidanceLevel(withIdentity, identity.guidanceLevel);
          setState(next);
          // Remember the just-submitted identity so the next new-story
          // flow's CharacterSetup pre-fills these same selections — now
          // including age + drawn-to age range.
          setLastKnownIdentity({
            gender: identity.gender,
            drawnTo: identity.drawnTo,
            age: identity.age,
            drawnToAgeMin: identity.drawnToAgeMin,
            drawnToAgeMax: identity.drawnToAgeMax,
          });
          // Sticky guidance — next new-story's CharacterSetup pre-selects
          // this level too.
          setLastKnownGuidance(identity.guidanceLevel);
          void persist(next);
        }}
      />,
    );
  }

  // ── Name-your-story gate (only for freshly created stories) ───────────
  // After CharacterSetup but before the game UI: if this story was just
  // created from a ScenarioPicker pick, give the player a chance to name
  // it. The story already exists on the server with a placeholder name
  // ("Untitled"); we PATCH /api/stories/:id with the resolved name when
  // the user submits. Skipping uses the scenario's display name. Failures
  // toast and clear the flag — the player still gets into the game.
  if (currentStory && awaitingName) {
    // Prefer the scenario's thematic defaultStoryName (e.g. "Smoke and rain")
    // shipped via GET /api/scenarios. Fall through to the presentation-only
    // pillLabel if the catalogue hasn't loaded yet (rare; defensive) and
    // finally to a generic string.
    const fromCatalogue = scenarioCatalogue.find(
      (s) => s.id === currentStory.scenarioId,
    );
    const scenarioName =
      fromCatalogue?.defaultStoryName
      ?? SCENARIO_PRESENTATIONS[currentStory.scenarioId]?.pillLabel
      ?? "your story";
    return themed(
      <NamePrompt
        scenarioName={scenarioName}
        onComplete={(resolvedName) => {
          void (async () => {
            try {
              const { story } = await renameStory(
                currentStory.id,
                resolvedName,
              );
              setCurrentStory((cs) =>
                cs ? { ...cs, name: story.name } : cs,
              );
              setAllStories((all) =>
                all.map((s) => (s.id === story.id ? story : s)),
              );
            } catch (err) {
              const msg =
                err instanceof StoriesApiError
                  ? err.message
                  : err instanceof Error
                    ? err.message
                    : String(err);
              console.warn("[stories] rename failed:", msg);
              pushToast(t("common:toasts.couldntSaveName", { message: shortErr(msg) }));
              // Don't block the user — they keep the placeholder name
              // (or whatever was on the row) and drop into the game.
            } finally {
              setAwaitingName(false);
            }
          })();
        }}
      />,
    );
  }

  // ── Billing return screen (success / cancel from Stripe) ──────────────
  // Single-state branch; once dismissed, the URL is cleaned and we drop
  // back into the regular game UI. We refetch billing status when entering
  // the success branch so the pill is current as soon as the user returns
  // to the game.
  if (billingReturn) {
    return themed(
      <BillingReturnScreen
        kind={billingReturn}
        onDismiss={async () => {
          if (billingReturn === "success") {
            await refreshBilling();
          }
          clearBillingReturnFromUrl();
          setBillingReturn(null);
        }}
      />,
    );
  }

  const balance = billing.balance;
  const balanceTier: "normal" | "warn" | "danger" =
    balance === null
      ? "normal"
      : balance < 10
        ? "danger"
        : balance < 50
          ? "warn"
          : "normal";
  const balanceValueStyle =
    balanceTier === "danger"
      ? styles.metaValueDanger
      : balanceTier === "warn"
        ? styles.metaValueWarn
        : styles.metaValue;
  const balanceText = balance === null ? "—" : String(balance);

  const subscription = billing.subscription;
  const canManageSub =
    subscription !== null && subscription.status !== "canceled";

  return themed(
    <div style={styles.shell} className="ember-game-shell">
      {/* Responsive two-pane layout.
          - Narrow (<900px wide, eg phone portrait + iPad portrait):
              chrome sits as a top strip, content fills below — the
              pre-existing single-column experience.
          - Wide (≥900px, eg iPad landscape, laptops, desktops):
              chrome moves into a fixed-width LEFT sidebar with the
              TrackingMap inline; right pane is purely narration + input.
          Inline styles win over CSS by default, so the wide rules use
          !important to override the existing styles.header / headerMeta
          / main inline declarations at the breakpoint. */}
      <style>{`
        .ember-game-shell .ember-game-map-inset { display: none; }
        @media (min-width: 900px) {
          .ember-game-shell .ember-game-twocol {
            flex-direction: row !important;
            align-items: stretch;
          }
          .ember-game-shell .ember-game-sidebar {
            flex: 0 0 320px !important;
            max-width: 360px;
            padding: 22px 22px 22px !important;
            border-bottom: none !important;
            border-right: 1px solid var(--paper-edge, rgba(212,165,116,0.18)) !important;
            overflow-y: auto !important;
            height: auto !important;
          }
          .ember-game-shell .ember-game-meta {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 8px !important;
          }
          .ember-game-shell .ember-game-map-inset {
            display: block;
            margin-top: 22px;
          }
          .ember-game-shell .ember-game-content {
            flex: 1 !important;
            min-width: 0 !important;
          }
        }
      `}</style>

      <div style={styles.grain} />

      {pendingDeletionAt && (
        <DeletionPendingBanner
          scheduledAt={pendingDeletionAt}
          onCancel={() => void handleCancelPendingDeletion()}
        />
      )}

      <div style={styles.gameTwoCol} className="ember-game-twocol">
        <header style={styles.header} className="ember-game-sidebar">
          <div style={styles.titleRow}>
            {currentStory ? (
              <TitleDropdown
                currentStory={currentStory}
                allStories={allStories}
                onChoose={(id) => void handleChooseStory(id)}
                onStartNew={handleStartNewStory}
              />
            ) : (
              <h1 style={styles.title}>Ember Hollow</h1>
            )}
            <div style={styles.subtitle}>
              {t("game:subtitle", {
                count: state.turnCount,
                boot: t(`game:boot.${bootStatusKey(bootStatus)}`, {
                  defaultValue: bootStatus,
                }),
              })}
              {!saveLoaded && ` · ${t("game:boot.syncing")}`}
            </div>
          </div>
          <div style={styles.headerMeta} className="ember-game-meta">
            <span style={styles.metaPill}>
              <span style={styles.metaLabel}>{t("game:header.here")}</span>
              <span style={styles.metaValue}>{cur?.name || t("game:header.noKnownRoom")}</span>
            </span>
            <KnownRoomsMenu state={state} />
            <CarryingMenu state={state} />
            <TurnsPill
              tier={balanceTier}
              text={balanceText}
              valueStyle={balanceValueStyle}
              onClick={() => {
                // Pill click is a manual open — marketing variant, not
                // story_locked / out_of_turns.
                setPaywallReason("manual");
                setShowPaywall(true);
              }}
            />
            <button
              type="button"
              onClick={toggleMuted}
              style={styles.headerAudioBtn}
              aria-label={muted ? t("common:audio.unmuteLabel") : t("common:audio.muteLabel")}
              title={muted ? t("common:audio.unmuteTitle") : t("common:audio.muteTitle")}
            >
              {muted ? "\u{1F507}" : "\u{1F50A}"}
            </button>
            {user && (
              <UserMenu
                user={user}
                canManageSub={canManageSub}
                guidanceLevel={state.guidance?.level ?? 0}
                spiceLevel={state.player?.spice ?? 1}
                // Gate the spice submenu on the logged-in user's
                // users.is_admin (returned by every auth response;
                // hydrated via /me on mount, persisted in AuthUser).
                // Server-side gate on POST /api/admin/spice is the
                // authoritative check; this just hides the UI from
                // non-admins so the trigger never even surfaces.
                isAdmin={user.isAdmin === true}
                onBuyTurns={handleBuyTurns}
                onManageSubscription={handleManageSubscription}
                onGuidanceChange={handleGuidanceChange}
                onSpiceChange={handleSpiceChange}
                onSwitchStory={handleOpenStoryPicker}
                onOpenBookshelf={handleOpenBookshelf}
                onOpenReferrals={handleOpenReferrals}
                onOpenAccountSettings={handleOpenAccountSettings}
                onOpenPlayerIdentity={handleOpenPlayerIdentity}
                onSignOut={onSignOut}
              />
            )}
          </div>

          {/* Inline tracking map — only visible on wide screens via the
              media query above. Click expands the full map modal (or
              focuses an already-open pop-out window if one's alive). */}
          <div className="ember-game-map-inset">
            <TrackingMap
              state={state}
              scenarioId={currentStory?.scenarioId ?? null}
              npcs={state.npcs}
              onOpen={handleOpenMap}
              movementPath={pendingMovementPath}
            />
          </div>
        </header>

        <div style={styles.main} className="ember-game-content">
          <div style={styles.leftColumn}>
            <div
              style={styles.logPane}
              ref={logRef}
              onClick={skipNarrationAnimation}
            >
              {state.log.map((entry, i) => (
                <LogLine
                  key={i}
                  entry={entry}
                  animate={i === freshNarrationIdx && entry.kind === "narration"}
                  onRetry={(text) => {
                    void handleSubmit(undefined, text);
                  }}
                />
              ))}
              {busy && (
                <div style={styles.thinking}>
                  <span style={styles.thinkingDot} />
                  <span style={{ ...styles.thinkingDot, animationDelay: "0.2s" }} />
                  <span style={{ ...styles.thinkingDot, animationDelay: "0.4s" }} />
                  {/* Phase 2b seam: the winning turn runs two model calls
                      server-side (~2x latency). Relabelling this to
                      "composing your finale…" needs a server signal we don't
                      have pre-response, so it's deferred. The
                      game:thinkingFinale key is staged for that wiring. */}
                  <span style={styles.thinkingText}>
                    {/* Per-turn rotating phrase (scenario-flavoured + generic),
                        chosen once at turn start in handleSubmit. Falls back to
                        the legacy static string if busy was flipped by some path
                        that didn't set a phrase. */}
                    {thinkingPhrase || t("game:thinking")}
                  </span>
                </div>
              )}
              {state.gameOver && (() => {
                const theme =
                  SCENARIO_PRESENTATIONS[currentStory?.scenarioId ?? ""]?.theme;
                return (
                  <Finale
                    outcome={state.gameOver === "win" ? "win" : "death"}
                    storyName={currentStory?.name ?? ""}
                    accent={theme?.accent ?? "#d4a574"}
                    accentMuted={theme?.accentMuted ?? "rgba(212,165,116,0.45)"}
                    ink={theme?.ink ?? "#e8d9b8"}
                    inkMuted={theme?.inkMuted ?? "#8a7355"}
                    paper={theme?.paper ?? "rgba(20,14,10,0.55)"}
                    paperEdge={theme?.paperEdge ?? "rgba(212,165,116,0.18)"}
                    fontFamily={
                      theme?.fontFamily ??
                      "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif"
                    }
                    onReadBook={handleFinaleReadBook}
                    onContinueFromPoint={handleFinaleContinueFromPoint}
                    onShare={handleFinaleShare}
                    onRestart={handleFinaleRestart}
                    onNewStory={handleFinaleNewStory}
                    // Phase 3b — the "next book" slate: captured from the
                    // winning /api/turn response or fetched (reopened finale)
                    // via getSequelOptions. The card renders the id/structure/
                    // title/pitch subset; App holds the FULL objects so
                    // onBeginSequel can POST the chosen one to spawn Book N+1.
                    sequelOptions={finaleSequelOptions}
                    onBeginSequel={handleBeginSequel}
                    // Phase 2b seam: shareBlurb / shareUrl intentionally
                    // unset — FB + copy share text falls back to the title,
                    // and the FB button publishes-first via onShare().
                  />
                );
              })()}
            </div>

            <form style={styles.inputBar} onSubmit={handleSubmit}>
              <span style={styles.prompt}>&gt;</span>
              <input
                ref={inputRef}
                style={styles.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                disabled={busy}
                placeholder={busy ? t("game:input.placeholderBusy") : t("game:input.placeholderPrompt")}
                autoFocus
                spellCheck={false}
                maxLength={2000}
              />
              <button
                type="button"
                onClick={() => handleSubmit()}
                style={{
                  ...styles.submitBtn,
                  opacity: busy || !input.trim() ? 0.3 : 1,
                  cursor: busy || !input.trim() ? "default" : "pointer",
                }}
                disabled={busy || !input.trim()}
              >
                ↵
              </button>
            </form>
          </div>

          {showDebug && (
            <DebugPane state={state} lastError={lastError} bootStatus={bootStatus} />
          )}
        </div>
      </div>

      {/* Map modal — triggered by the in-game `map` command or the inset
          click. The "Pop out" button on the modal header opens a second-
          window MapWindow on the same origin; handleOpenMapPopout closes
          this modal automatically once the window opens. */}
      {showMap && (
        <FullMap
          state={state}
          scenarioId={currentStory?.scenarioId ?? null}
          npcs={state.npcs}
          onClose={() => setShowMap(false)}
          onPopOut={currentStory ? handleOpenMapPopout : undefined}
          movementPath={pendingMovementPath}
        />
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div style={styles.toastWrap} aria-live="polite">
          {toasts.map((t) => {
            const clickable = Boolean(t.action);
            const variantStyle: CSSProperties =
              t.variant === "success"
                ? {
                    borderColor: "rgba(154,214,177,0.5)",
                    color: "#c7e8d2",
                  }
                : t.variant === "warn"
                  ? {
                      borderColor: "rgba(232,193,128,0.55)",
                      color: "#e8c180",
                    }
                  : {};
            const clickableStyle: CSSProperties = clickable
              ? { cursor: "pointer" }
              : {};
            return (
              <div
                key={t.id}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : -1}
                onClick={
                  clickable
                    ? () => {
                        t.action?.onClick();
                      }
                    : undefined
                }
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          t.action?.onClick();
                        }
                      }
                    : undefined
                }
                style={{ ...styles.toast, ...variantStyle, ...clickableStyle }}
              >
                <span>{t.text}</span>
                {clickable && (
                  <span
                    style={{
                      marginLeft: 8,
                      opacity: 0.85,
                      textDecoration: "underline",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.action?.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Paywall modal */}
      {showPaywall && (
        <Paywall
          subscription={billing.subscription}
          balance={billing.balance}
          reason={paywallReason}
          onClose={() => {
            setShowPaywall(false);
            // Reset to manual so the next implicit open (out_of_turns,
            // story_locked, story_cap) sets the reason explicitly rather
            // than reusing a stale one.
            setPaywallReason("manual");
          }}
          onExtensionGranted={() => {
            void refreshBilling();
            pushToast(t("common:toasts.extensionGranted"));
          }}
          onRefreshStatus={async () => {
            await refreshBilling();
            // If the refresh shows balance>0 the user can keep playing;
            // close the modal automatically. Otherwise leave it open so
            // they can still pick a CTA.
            // We read the just-set state via a microtask; simplest is to
            // re-fetch directly here and close on positive balance.
            try {
              const status = await getBillingStatus();
              setBilling({
                balance: status.balance,
                subscription: status.subscription,
                loaded: true,
              });
              if (status.balance > 0) setShowPaywall(false);
            } catch {
              // Already toasted/logged in refreshBilling — leave modal open.
            }
          }}
        />
      )}

      {/* StoryPicker as mid-game switch surface. The picker fills the
          viewport (z-index inherent to its full-screen layout) and exposes
          an X — the user is back in the game when they choose / close. */}
      {showStoryPicker && currentStory && (
        <StoryPicker
          stories={allStories}
          activeStoryId={currentStory.id}
          onChoose={(id) => {
            void handleChooseStory(id);
          }}
          onStartNew={handleStartNewStory}
          onDelete={(id) => {
            void handleDeleteStory(id);
          }}
          onRename={async (id, name) => {
            // Errors propagate to StoryPicker so it can keep the row in
            // edit mode and surface an inline message. Mirror the renamed
            // row + the currentStory pointer (if it's the active story
            // being renamed) so the header pill updates immediately.
            const { story } = await renameStory(id, name);
            setAllStories((all) =>
              all.map((s) => (s.id === id ? story : s)),
            );
            setCurrentStory((cs) =>
              cs && cs.id === id ? { ...cs, name: story.name } : cs,
            );
          }}
          onClose={() => setShowStoryPicker(false)}
        />
      )}

      {/* ScenarioPicker as mid-game modal on top of the game UI (and on
          top of the StoryPicker when both are open during "Start a new
          story"). Its own backdrop handles dismiss-on-outside-click. */}
      {showScenarioPicker && currentStory && (
        <ScenarioPicker
          open={true}
          scenarios={scenarioCatalogue}
          onPick={(scenarioId, language) => {
            void handlePickScenario(scenarioId, language);
          }}
          onLockedClicked={() => {
            setShowScenarioPicker(false);
            setPaywallReason("story_locked");
            setShowPaywall(true);
          }}
          onClose={() => setShowScenarioPicker(false)}
          // Mid-game path. By definition the user already has at least
          // one story (currentStory is truthy), so they've already burned
          // their forever_default — no first-pick warning to show.
          firstPickWarning={false}
          accountLanguage={user?.language ?? null}
        />
      )}

      {/* Bookshelf — full-page surface listing all the user's stories as
          books, opened from the user-chip menu. Renders above the game UI;
          dismissed via its own close button. */}
      {showBookshelf && (
        <Bookshelf
          stories={allStories}
          authorName={user?.name || user?.email || "an unnamed dreamer"}
          onRead={(storyId) => {
            setBookReader({ storyId, chapterNumber: 1 });
          }}
          onContinuePlaying={(storyId) => {
            setShowBookshelf(false);
            setBookReader(null);
            if (currentStory && currentStory.id === storyId) {
              return;
            }
            void handleChooseStory(storyId);
          }}
          onClose={() => setShowBookshelf(false)}
        />
      )}

      {/* Referrals dashboard — Phase 3 account-area surface. Opened from
          the user-chip menu and from any referral-bonus action toast. */}
      {showReferrals && (
        <ReferralsPage
          onClose={() => setShowReferrals(false)}
          allStories={allStories}
        />
      )}

      {/* Player identity edit — gender, drawnTo, age, drawn-to age
          range. Opened from the user-chip menu. Saves via the engine's
          applyPlayerSetup helper + the existing per-story persist
          path, mirroring how CharacterSetup's first-run submission
          flows. Updates lastKnownIdentity so subsequent new-story
          flows pre-fill the new values. */}
      {showPlayerIdentity && (
        <PlayerIdentityEdit
          state={state}
          onSave={(next) => {
            setState(next);
            setLastKnownIdentity({
              gender: next.player.gender,
              drawnTo: next.player.drawnTo,
              age: next.player.age,
              drawnToAgeMin: next.player.drawnToAgeMin,
              drawnToAgeMax: next.player.drawnToAgeMax,
            });
            void persist(next);
          }}
          onClose={() => setShowPlayerIdentity(false)}
        />
      )}

      {/* Account settings — currently hosts only the Delete-my-account
          flow. Updates the persistent banner state on confirmation. */}
      {showAccountSettings && (
        <AccountSettingsPage
          onClose={() => {
            setShowAccountSettings(false);
            // Re-pull the pending status so the banner reflects any
            // change the user just made inside the page.
            void (async () => {
              try {
                const res = await getAccountDeletionStatus();
                setPendingDeletionAt(
                  res.pending && res.scheduledAt ? res.scheduledAt : null,
                );
              } catch {
                // ignore — banner stays whatever it was.
              }
            })();
          }}
          onToast={(text, ttl) => pushToast(text, ttl)}
          currentLanguage={user?.language ?? null}
          onLanguageChanged={(code) => onPatchUser({ language: code })}
        />
      )}

      {/* BookReader — chapter-by-chapter reading view. Opens from the
          bookshelf row OR from a chapter-toast click. Closes back to the
          bookshelf (or to the game if the bookshelf wasn't open). */}
      {bookReader && (() => {
        const story = allStories.find((s) => s.id === bookReader.storyId);
        const sceneId = story?.scenarioId ?? currentStory?.scenarioId ?? "ember_hollow";
        return (
          <BookReader
            storyId={bookReader.storyId}
            scenarioId={sceneId}
            storyName={story?.name || "Untitled"}
            authorName={user?.name || user?.email || "an unnamed dreamer"}
            initialChapterNumber={bookReader.chapterNumber}
            onBack={() => {
              setBookReader(null);
              // If the user opened the reader from a toast (bookshelf
              // wasn't open), there's nothing underneath to return to —
              // leave them on the game. If the bookshelf is open, the
              // close just dismisses the reader.
            }}
            onContinuePlaying={() => {
              setBookReader(null);
              setShowBookshelf(false);
              if (currentStory && currentStory.id !== bookReader.storyId) {
                void handleChooseStory(bookReader.storyId);
              }
            }}
            showBranchActions={bookReader.branchEnabled === true}
            onForkToStory={(newStoryId) => {
              // Private fork succeeded — close the reader and switch the
              // player into the new continuation via the existing
              // activate-story path. Nothing was published.
              // The Finale panel is gated by state.gameOver; switching into
              // the new (non-finished) story clears that, so no explicit
              // Finale dismissal is needed here.
              setBookReader(null);
              setShowBookshelf(false);
              void (async () => {
                await handleChooseStory(newStoryId);
                pushToast({
                  variant: "success",
                  text: t("game:finale.continueForked", {
                    defaultValue: "Branched a new private continuation.",
                  }),
                });
              })();
            }}
            onError={(message) => pushToast(message)}
          />
        );
      })()}

      {/* ShareModal — opened from the inline Finale panel's "Share" /
          Facebook actions. Same component the Bookshelf uses; resolves the
          scenario theme tokens the same way. */}
      {finaleShareStoryId && (() => {
        const story =
          allStories.find((s) => s.id === finaleShareStoryId) ?? null;
        const sceneId =
          story?.scenarioId ?? currentStory?.scenarioId ?? "ember_hollow";
        const presentation = SCENARIO_PRESENTATIONS[sceneId];
        const theme = presentation?.theme;
        return (
          <ShareModal
            storyId={finaleShareStoryId}
            storyName={
              story?.name ||
              currentStory?.name ||
              t("bookshelf:bookReader.untitled", { defaultValue: "Untitled" })
            }
            authorName={user?.name || user?.email || "an unnamed dreamer"}
            scenarioName={t(`scenarios:${sceneId}.pillLabel`, {
              defaultValue: presentation?.pillLabel ?? sceneId,
            })}
            accent={theme?.accent ?? "#d4a574"}
            accentMuted={theme?.accentMuted ?? "rgba(212,165,116,0.45)"}
            ink={theme?.ink ?? "#e8d9b8"}
            inkMuted={theme?.inkMuted ?? "#8a7355"}
            paper={theme?.paper ?? "rgba(20,14,10,0.55)"}
            paperEdge={theme?.paperEdge ?? "rgba(212,165,116,0.18)"}
            fontFamily={
              theme?.fontFamily ??
              "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif"
            }
            onClose={() => setFinaleShareStoryId(null)}
          />
        );
      })()}

      <style>{`
        @keyframes flicker { 0%,100%{opacity:1} 50%{opacity:0.92} }
        @keyframes pulse { 0%,100%{transform:scale(1);opacity:0.4} 50%{transform:scale(1.4);opacity:1} }
        @keyframes fadein { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes narrationWordIn { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          .ember-log-entry span { animation: none !important; opacity: 1 !important; }
        }
        .ember-log-entry { animation: fadein 0.4s ease-out; }
      `}</style>
    </div>
  );
}

// ── Subcomponents ───────────────────────────────────────────────────────

/**
 * Persistent "deletion scheduled" banner. Renders at the very top of the
 * shell while account_deletions has a pending row for the current user.
 * Cancel button calls POST /api/account/cancel-deletion.
 */
function DeletionPendingBanner({
  scheduledAt,
  onCancel,
}: {
  scheduledAt: string;
  onCancel: () => void;
}) {
  const { t } = useTranslation(["common"]);
  const dateText = (() => {
    try {
      const d = new Date(scheduledAt);
      if (Number.isNaN(d.getTime())) return scheduledAt;
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return scheduledAt;
    }
  })();
  const bannerStyle: CSSProperties = {
    position: "relative",
    zIndex: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: "8px 16px",
    background: "rgba(232,193,128,0.12)",
    borderBottom: "1px solid rgba(232,193,128,0.4)",
    color: "#e8c180",
    fontSize: "0.85rem",
    fontFamily: "inherit",
  };
  const btnStyle: CSSProperties = {
    background: "transparent",
    border: "1px solid rgba(232,193,128,0.55)",
    color: "#e8c180",
    padding: "4px 10px",
    fontSize: "0.78rem",
    borderRadius: 2,
    cursor: "pointer",
    fontFamily: "inherit",
  };
  // Banner uses the translation key + a literal `<strong>` tag in the
  // translated string; split-render so the date sits inside an inline
  // <strong>. Falls back to inline rendering if the tag is missing.
  const raw = t("common:deletionBanner.scheduledFor", { date: dateText });
  const open = "<strong>";
  const close = "</strong>";
  const a = raw.indexOf(open);
  const b = a >= 0 ? raw.indexOf(close, a + open.length) : -1;
  let body: JSX.Element;
  if (a < 0 || b < 0) {
    body = <span>{raw}</span>;
  } else {
    const before = raw.slice(0, a);
    const inside = raw.slice(a + open.length, b);
    const after = raw.slice(b + close.length);
    body = (
      <span>
        {before}
        <strong>{inside}</strong>
        {after}
      </span>
    );
  }
  return (
    <div style={bannerStyle} role="status">
      {body}
      <button type="button" style={btnStyle} onClick={onCancel}>
        {t("common:buttons.cancel")}
      </button>
    </div>
  );
}

// ── Header pills with hover dropdowns ───────────────────────────────────

function TurnsPill({
  tier,
  text,
  valueStyle,
  onClick,
}: {
  tier: "normal" | "warn" | "danger";
  text: string;
  valueStyle: CSSProperties;
  onClick: () => void;
}) {
  const { t } = useTranslation("game");
  const [hover, setHover] = useState(false);
  const base =
    tier === "danger"
      ? styles.metaPillButtonDanger
      : tier === "warn"
        ? styles.metaPillButtonWarn
        : styles.metaPillButton;
  const hoverOverlay =
    tier === "danger"
      ? styles.metaPillButtonDangerHover
      : tier === "warn"
        ? styles.metaPillButtonWarnHover
        : styles.metaPillButtonHover;
  return (
    <button
      type="button"
      style={{ ...base, ...(hover ? hoverOverlay : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      onClick={onClick}
      title={t("header.turnsTitle")}
    >
      <span style={styles.metaLabel}>{t("header.turns")}</span>
      <span style={valueStyle}>{text}</span>
    </button>
  );
}

// TitleDropdown — clickable header title that doubles as a fast story
// switcher. Replaces the previously-static "Ember Hollow" h1. The label
// shows the current story's scenario name (Ember Hollow / Verity Drift /
// etc.) so it's immediately obvious which world you're in.
function TitleDropdown({
  currentStory,
  allStories,
  onChoose,
  onStartNew,
}: {
  currentStory: CurrentStory;
  allStories: StoryListEntry[];
  onChoose: (id: string) => void;
  onStartNew: () => void;
}) {
  const { open, triggerProps, menuProps, wrapRef, close } = useHoverMenu();
  const { t } = useTranslation(["scenarios", "game"]);

  const presentation = SCENARIO_PRESENTATIONS[currentStory.scenarioId];
  // Use the translated pillLabel when available; fall back to the
  // structural one shipped in presentation.ts.
  const titleLabel = t(`scenarios:${currentStory.scenarioId}.pillLabel`, {
    defaultValue: presentation?.pillLabel ?? "Ember Hollow",
  });

  // Sort: current first, then by lastPlayedAt desc.
  const sortedStories = useMemo(() => {
    const cur = allStories.filter((s) => s.id === currentStory.id);
    const others = allStories
      .filter((s) => s.id !== currentStory.id)
      .sort((a, b) => b.lastPlayedAt.localeCompare(a.lastPlayedAt));
    return [...cur, ...others];
  }, [allStories, currentStory.id]);

  return (
    <span ref={(el) => wrapRef(el)} style={titleDropdownWrap}>
      <button
        type="button"
        style={open ? { ...titleDropdownButton, ...titleDropdownButtonOpen } : titleDropdownButton}
        aria-haspopup="menu"
        aria-expanded={open}
        {...triggerProps}
      >
        {titleLabel}
        <span style={titleDropdownChevron} aria-hidden>▾</span>
      </button>
      {open && (
        <div
          style={titleDropdownMenu}
          role="menu"
          tabIndex={-1}
          {...menuProps}
        >
          <div
            style={titleDropdownNew}
            onClick={() => {
              close();
              onStartNew();
            }}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                close();
                onStartNew();
              }
            }}
          >
            {t("game:titleDropdown.newStory")}
          </div>
          <div style={titleDropdownSeparator} aria-hidden />
          {sortedStories.length === 0 ? (
            <div style={titleDropdownEmpty}>{t("game:titleDropdown.noStories")}</div>
          ) : (
            sortedStories.map((s) => {
              const isCurrent = s.id === currentStory.id;
              const sp = SCENARIO_PRESENTATIONS[s.scenarioId];
              const scenarioLabel = t(`scenarios:${s.scenarioId}.pillLabel`, {
                defaultValue: sp?.pillLabel ?? s.scenarioId,
              });
              return (
                <div
                  key={s.id}
                  style={
                    isCurrent
                      ? { ...titleDropdownStory, ...titleDropdownStoryCurrent }
                      : titleDropdownStory
                  }
                  onClick={() => {
                    close();
                    if (!isCurrent) onChoose(s.id);
                  }}
                  role="menuitem"
                  tabIndex={isCurrent ? -1 : 0}
                  onKeyDown={(e) => {
                    if (!isCurrent && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      close();
                      onChoose(s.id);
                    }
                  }}
                >
                  <div style={titleDropdownStoryName}>
                    {isCurrent ? "▸ " : "  "}
                    {s.name}
                  </div>
                  <div style={titleDropdownStoryScenario}>{scenarioLabel}</div>
                </div>
              );
            })
          )}
        </div>
      )}
    </span>
  );
}

// Title-dropdown styles read theme vars (with Ember Hollow fallbacks) so the
// dropdown re-skins together with the rest of the in-game UI when the user
// switches scenarios. Same fallback shape as styles.ts.
const titleDropdownWrap: CSSProperties = {
  position: "relative",
  display: "inline-block",
};

const titleDropdownButton: CSSProperties = {
  margin: 0,
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontSize: "2.4rem",
  fontWeight: 400,
  color: "var(--ink, #e8d9b8)",
  letterSpacing: "0.02em",
  textShadow: "0 0 24px rgba(0,0,0,0.4)",
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "baseline",
  gap: 10,
  textAlign: "left",
  transition: "color 0.4s ease-out",
};

const titleDropdownButtonOpen: CSSProperties = {
  color: "var(--accent, #d4a574)",
};

const titleDropdownChevron: CSSProperties = {
  fontSize: "1.1rem",
  color: "var(--accent, #d4a574)",
  marginLeft: 0,
  transform: "translateY(-2px)",
  transition: "color 0.4s ease-out",
};

const titleDropdownMenu: CSSProperties = {
  position: "absolute",
  top: "100%",
  left: 0,
  marginTop: 6,
  background: "var(--paper, rgba(20,14,10,0.97))",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  boxShadow: "0 8px 28px rgba(0,0,0,0.7)",
  minWidth: 280,
  maxHeight: 420,
  overflowY: "auto",
  zIndex: 30,
  padding: "6px 0",
  transition:
    "background 0.4s ease-out, color 0.4s ease-out, border-color 0.4s ease-out",
};

const titleDropdownNew: CSSProperties = {
  padding: "10px 16px",
  cursor: "pointer",
  color: "var(--accent, #d4a574)",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.8rem",
  letterSpacing: "0.08em",
  textTransform: "lowercase",
  transition: "color 0.4s ease-out",
};

const titleDropdownSeparator: CSSProperties = {
  height: 1,
  background: "var(--paper-edge, rgba(212,165,116,0.2))",
  margin: "4px 0",
  transition: "background 0.4s ease-out",
};

const titleDropdownEmpty: CSSProperties = {
  padding: "12px 16px",
  color: "var(--ink-muted, #7a6243)",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.8rem",
  fontStyle: "italic",
  transition: "color 0.4s ease-out",
};

const titleDropdownStory: CSSProperties = {
  padding: "10px 16px",
  cursor: "pointer",
  color: "var(--ink-muted, #a89070)",
  letterSpacing: "0.02em",
  transition: "color 0.4s ease-out, background 0.4s ease-out",
};

const titleDropdownStoryCurrent: CSSProperties = {
  background: "var(--paper, rgba(212,165,116,0.06))",
  color: "var(--ink, #e8c896)",
  cursor: "default",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
};

const titleDropdownStoryName: CSSProperties = {
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', 'IM Fell English', Georgia, serif)",
  fontSize: "1.05rem",
  marginBottom: 2,
};

const titleDropdownStoryScenario: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.68rem",
  color: "var(--ink-muted, #7a6243)",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  transition: "color 0.4s ease-out",
};

function KnownRoomsMenu({ state }: { state: GameState }) {
  const { open, triggerProps, menuProps, wrapRef, close } = useHoverMenu();
  const { t } = useTranslation(["game", "common"]);

  // Sort: current room first, then in insertion order (most-recently-added
  // first — newer rooms tend to be where the player just was). We can't
  // know true visit recency without timestamps; insertion order is the
  // best proxy and matches how the model adds rooms turn-by-turn.
  const sortedRooms = useMemo(() => {
    const all = Object.values(state.rooms);
    const currentId = state.currentRoom;
    const current = all.filter((r) => r.id === currentId);
    const others = all.filter((r) => r.id !== currentId).reverse();
    return [...current, ...others];
  }, [state.rooms, state.currentRoom]);

  const count = Object.keys(state.rooms).length;

  return (
    <span
      style={styles.metaPillWrap}
      ref={(el) => wrapRef(el)}
    >
      <button
        type="button"
        style={{
          ...styles.metaPillButton,
          ...(open ? styles.metaPillButtonHover : {}),
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        {...triggerProps}
      >
        <span style={styles.metaLabel}>{t("game:header.known")}</span>
        <span style={styles.metaValue}>
          {t("common:room", { count, defaultValue: `${count} rooms` })}
        </span>
      </button>
      {open && (
        <div
          style={styles.headerMenu}
          role="menu"
          tabIndex={-1}
          {...menuProps}
        >
          {sortedRooms.length === 0 ? (
            <div style={styles.headerMenuEmpty}>{t("game:header.noKnownRoom")}</div>
          ) : (
            sortedRooms.map((r) => {
              const isCurrent = r.id === state.currentRoom;
              const exits = Object.entries(r.exits)
                .filter(([, v]) => Boolean(v))
                .map(([dir]) => dir)
                .join(" · ");
              return (
                <div
                  key={r.id}
                  style={{
                    ...styles.headerMenuRoom,
                    ...(isCurrent ? styles.headerMenuRoomCurrent : {}),
                  }}
                  onClick={close}
                  role="menuitem"
                >
                  <div
                    style={{
                      ...styles.headerMenuRoomName,
                      ...(isCurrent
                        ? styles.headerMenuRoomNameCurrent
                        : {}),
                    }}
                  >
                    {isCurrent ? "▸ " : "  "}
                    {r.name}
                  </div>
                  <div style={styles.headerMenuRoomExits}>
                    {exits || t("game:header.noKnownRoom")}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </span>
  );
}

function CarryingMenu({ state }: { state: GameState }) {
  const { open, triggerProps, menuProps, wrapRef } = useHoverMenu();
  const { t } = useTranslation(["game", "common"]);
  const items = state.inventory
    .map((id) => state.items[id])
    .filter(Boolean);
  const count = items.length;

  return (
    <span style={styles.metaPillWrap} ref={(el) => wrapRef(el)}>
      <button
        type="button"
        style={{
          ...styles.metaPillButton,
          ...(open ? styles.metaPillButtonHover : {}),
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        {...triggerProps}
      >
        <span style={styles.metaLabel}>{t("game:header.carrying")}</span>
        <span style={styles.metaValue}>
          {count === 0
            ? t("game:header.nothing")
            : t("common:item", { count, defaultValue: `${count} items` })}
        </span>
      </button>
      {open && (
        <div
          style={styles.headerMenu}
          role="menu"
          tabIndex={-1}
          {...menuProps}
        >
          {count === 0 ? (
            <div style={styles.headerMenuEmpty}>
              {t("game:header.carryNothingYet")}
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.id}
                style={styles.headerMenuItem}
                role="menuitem"
              >
                <div style={styles.headerMenuItemName}>{it.name}</div>
                {it.description && (
                  <div style={styles.headerMenuItemDesc}>
                    {it.description}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </span>
  );
}

function UserMenu({
  user,
  canManageSub,
  guidanceLevel,
  spiceLevel,
  isAdmin,
  onBuyTurns,
  onManageSubscription,
  onGuidanceChange,
  onSpiceChange,
  onSwitchStory,
  onOpenBookshelf,
  onOpenReferrals,
  onOpenAccountSettings,
  onOpenPlayerIdentity,
  onSignOut,
}: {
  user: AuthUser;
  canManageSub: boolean;
  guidanceLevel: number;
  spiceLevel: number;
  isAdmin: boolean;
  onBuyTurns: () => void;
  onManageSubscription: () => void | Promise<void>;
  onGuidanceChange: (level: number) => void;
  onSpiceChange: (level: number) => void;
  onSwitchStory: () => void;
  onOpenBookshelf: () => void;
  onOpenReferrals: () => void;
  onOpenAccountSettings: () => void;
  onOpenPlayerIdentity: () => void;
  onSignOut: () => void;
}) {
  const { open, triggerProps, menuProps, wrapRef, close } = useHoverMenu();
  const { t } = useTranslation(["game", "common"]);
  // Local state — the guidance submenu is collapsed by default and
  // expands inline beneath its trigger row when the user clicks/Enters.
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  // Same pattern for the spice submenu (admin-only).
  const [spiceOpen, setSpiceOpen] = useState(false);

  // Reset the submenu state whenever the parent menu fully closes so it
  // opens fresh next time. Avoids the submenu being "stuck open" after a
  // hover-out + hover-in cycle.
  useEffect(() => {
    if (!open) {
      setGuidanceOpen(false);
      setSpiceOpen(false);
    }
  }, [open]);

  const handle = (fn: () => void | Promise<void>) => () => {
    close();
    void fn();
  };

  const pickGuidance = (level: number) => () => {
    onGuidanceChange(level);
    close();
  };

  const pickSpice = (level: number) => () => {
    onSpiceChange(level);
    close();
  };

  // Spice options for the admin-only override slider. The labels are
  // admin-facing; real players never see this menu — their spice adapts
  // silently via the model's setSpice delta based on engagement.
  const spiceOptions: Array<{ label: string; level: number }> = [
    { label: t("game:spice.vanilla"), level: 0 },
    { label: t("game:spice.mild"), level: 1 },
    { label: t("game:spice.spicy"), level: 2 },
    { label: t("game:spice.forward"), level: 3 },
  ];

  const guidanceOptions: Array<{
    label: string;
    desc: string;
    level: number;
  }> = [
    {
      label: t("game:guidance.story"),
      desc: t("game:guidance.storyDesc"),
      level: 2,
    },
    {
      label: t("game:guidance.guided"),
      desc: t("game:guidance.guidedDesc"),
      level: 1,
    },
    {
      label: t("game:guidance.classic"),
      desc: t("game:guidance.classicDesc"),
      level: 0,
    },
    {
      label: t("game:guidance.sparse"),
      desc: t("game:guidance.sparseDesc"),
      level: -1,
    },
    {
      label: t("game:guidance.expert"),
      desc: t("game:guidance.expertDesc"),
      level: -2,
    },
  ];

  return (
    <span style={styles.userMenuWrap} ref={(el) => wrapRef(el)}>
      <span style={styles.userChip}>
        <span style={styles.metaLabel}>{t("game:header.you")}</span>
        <button
          type="button"
          style={styles.userChipBtn}
          aria-haspopup="menu"
          aria-expanded={open}
          title={t("game:header.accountTitle")}
          {...triggerProps}
        >
          {user.name || user.email} ▾
        </button>
      </span>
      {open && (
        <div
          style={{ ...styles.userMenu }}
          role="menu"
          tabIndex={-1}
          {...menuProps}
        >
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onBuyTurns)}
            role="menuitem"
          >
            {t("game:userMenu.buyTurns")}
          </button>
          {canManageSub && (
            <button
              type="button"
              style={styles.userMenuItem}
              onClick={handle(onManageSubscription)}
              role="menuitem"
            >
              {t("game:userMenu.manageSubscription")}
            </button>
          )}
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onSwitchStory)}
            role="menuitem"
          >
            {t("game:userMenu.switchStory")}
          </button>
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onOpenBookshelf)}
            role="menuitem"
          >
            {t("game:userMenu.bookshelf")}
          </button>
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onOpenReferrals)}
            role="menuitem"
          >
            {t("game:userMenu.referrals")}
          </button>
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onOpenPlayerIdentity)}
            role="menuitem"
          >
            {t("game:userMenu.playerIdentity")}
          </button>
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onOpenAccountSettings)}
            role="menuitem"
          >
            {t("game:userMenu.account")}
          </button>
          <button
            type="button"
            style={styles.userMenuItem}
            aria-haspopup="menu"
            aria-expanded={guidanceOpen}
            onClick={() => setGuidanceOpen((v) => !v)}
            role="menuitem"
          >
            {t("game:userMenu.guidance", {
              label: t(`game:guidance.${guidanceLabel(guidanceLevel)}`),
            })}{" "}
            {guidanceOpen ? "▴" : "▾"}
          </button>
          {guidanceOpen &&
            guidanceOptions.map((opt) => {
              const selected = opt.level === guidanceLevel;
              return (
                <button
                  type="button"
                  key={opt.level}
                  role="menuitemradio"
                  aria-checked={selected}
                  style={{
                    ...styles.userMenuSubrow,
                    ...(selected ? styles.userMenuSubrowSelected : {}),
                    alignItems: "flex-start",
                  }}
                  onClick={pickGuidance(opt.level)}
                >
                  <span
                    style={{ ...styles.userMenuRadio, marginTop: 2 }}
                  >
                    {selected ? "●" : "○"}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <span>{opt.label}</span>
                    <span
                      style={{
                        fontSize: "0.7rem",
                        opacity: 0.75,
                        fontStyle: "italic",
                        fontFamily:
                          "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {opt.desc}
                    </span>
                  </span>
                </button>
              );
            })}
          {/* Spice override — ADMIN ONLY. Players never see this; their
              spice adapts silently via the model's setSpice delta based on
              observed engagement with kink-themed beats. The override
              exists purely so admins can land at a specific spice level
              without having to roleplay engagement to ramp it up. */}
          {isAdmin && (
            <button
              type="button"
              style={styles.userMenuItem}
              aria-haspopup="menu"
              aria-expanded={spiceOpen}
              onClick={() => setSpiceOpen((v) => !v)}
              role="menuitem"
            >
              {t("game:spice.adminHeading")} {spiceOpen ? "▴" : "▾"}
            </button>
          )}
          {isAdmin && spiceOpen &&
            spiceOptions.map((opt) => {
              const selected = opt.level === spiceLevel;
              return (
                <button
                  type="button"
                  key={opt.level}
                  role="menuitemradio"
                  aria-checked={selected}
                  style={{
                    ...styles.userMenuSubrow,
                    ...(selected ? styles.userMenuSubrowSelected : {}),
                  }}
                  onClick={pickSpice(opt.level)}
                >
                  <span style={styles.userMenuRadio}>
                    {selected ? "●" : "○"}
                  </span>
                  <span>{opt.label}</span>
                </button>
              );
            })}
          <div style={styles.userMenuDivider} />
          <button
            type="button"
            style={styles.userMenuItem}
            onClick={handle(onSignOut)}
            role="menuitem"
          >
            {t("game:userMenu.signOut")}
          </button>
        </div>
      )}
    </span>
  );
}

function LogLine({
  entry,
  animate,
  onRetry,
}: {
  entry: GameState["log"][number];
  animate?: boolean;
  onRetry?: (text: string) => void;
}) {
  const { t } = useTranslation();
  if (entry.kind === "input") {
    return (
      <div style={styles.inputLine} className="ember-log-entry">
        <span style={styles.inputArrow}>›</span>
        <span style={styles.inputText}>{entry.text}</span>
      </div>
    );
  }
  if (entry.kind === "system") {
    return (
      <div style={styles.systemLine} className="ember-log-entry">
        {entry.text}
      </div>
    );
  }
  if (entry.kind === "error") {
    // pendingInput is the UI-only flag on retryable errors. When present,
    // render an inline Retry button that resubmits the carried text via
    // the onRetry callback wired up at the .map() call site. Legacy error
    // entries (from saved games written before this field existed) have
    // no pendingInput and render as plain text — that's the right
    // fallback: we can't safely re-derive what the player typed.
    const retryable = typeof entry.pendingInput === "string" && entry.pendingInput.length > 0;
    return (
      <div style={styles.errorLine} className="ember-log-entry">
        <div>{entry.text}</div>
        {retryable && onRetry && (
          <button
            type="button"
            style={styles.errorRetryBtn}
            onClick={() => onRetry(entry.pendingInput as string)}
          >
            {t("common:buttons.retry")}
          </button>
        )}
      </div>
    );
  }
  return <NarrationLine text={entry.text} animate={!!animate} />;
}

// Per-turn narration is revealed word-by-word ~slightly-faster-than-fast-
// reading-speed (≈90ms/word with extra pauses after sentence-ending and
// clausal punctuation). Words pre-occupy their final layout (opacity 0 →
// 1 only — no transform), so line wrap and scroll position are stable
// from the first frame. `prefers-reduced-motion: reduce` is honoured via
// the @keyframes-disable rule in the page-level <style> block.
const WORD_STAGGER_MS = 90;
const PAUSE_AFTER_HARD_PUNCT_MS = 200;   // . ! ? — end of sentence
const PAUSE_AFTER_SOFT_PUNCT_MS = 80;    // , ; : — clausal break
const WORD_FADE_DURATION_MS = 360;        // overlaps with next stagger

function NarrationLine({
  text,
  animate,
}: {
  text: string;
  animate: boolean;
}) {
  // Static render: no animation needed, just plain text. Used for old log
  // entries that have already been read, and when reduced motion overrides.
  if (!animate) {
    return (
      <div style={styles.narration} className="ember-log-entry">
        {text}
      </div>
    );
  }

  // Tokenise on whitespace, preserving the whitespace tokens between words
  // so the original spacing is reproduced in the rendered output.
  const tokens = text.split(/(\s+)/);
  let cumulativeDelay = 0;

  return (
    <div style={styles.narration} className="ember-log-entry">
      {tokens.map((tok, i) => {
        // Whitespace tokens render flat — they don't fade because they
        // carry no glyphs. Keeps the inter-word spacing crisp.
        if (tok.length === 0) return null;
        if (/^\s+$/.test(tok)) {
          return <span key={i}>{tok}</span>;
        }
        const delay = cumulativeDelay;
        // Bump cumulative delay forward for the NEXT word.
        cumulativeDelay += WORD_STAGGER_MS;
        const lastChar = tok[tok.length - 1];
        if (lastChar === "." || lastChar === "!" || lastChar === "?") {
          cumulativeDelay += PAUSE_AFTER_HARD_PUNCT_MS;
        } else if (lastChar === "," || lastChar === ";" || lastChar === ":") {
          cumulativeDelay += PAUSE_AFTER_SOFT_PUNCT_MS;
        }
        return (
          <span
            key={i}
            style={{
              opacity: 0,
              animation: `narrationWordIn ${WORD_FADE_DURATION_MS}ms ease-out ${delay}ms forwards`,
            }}
          >
            {tok}
          </span>
        );
      })}
    </div>
  );
}

// MapPane (the old sidebar room-list) was retired when the 2D FullMap
// modal landed (2026-05-13). The in-game `map` command now toggles the
// FullMap modal (see App.tsx render branch). DebugPane is unchanged.

function DebugPane({
  state,
  lastError,
  bootStatus,
}: {
  state: GameState;
  lastError: string | null;
  bootStatus: BootStatus;
}) {
  const { t } = useTranslation("game");
  const debugStyle: CSSProperties = {
    ...styles.sidePane,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
  };
  return (
    <div style={debugStyle}>
      <div style={styles.mapTitle}>{t("debug.diagnostics")}</div>
      <div
        style={{
          marginBottom: 4,
          color: "var(--ink-muted, #7a6243)",
          transition: "color 0.4s ease-out",
        }}
      >
        {t("debug.boot", {
          status: t(`boot.${bootStatusKey(bootStatus)}`, { defaultValue: bootStatus }),
        })}
      </div>
      <div
        style={{
          marginBottom: 12,
          color: "var(--ink-muted, #7a6243)",
          transition: "color 0.4s ease-out",
        }}
      >
        {t("debug.guidanceLine", {
          label: t(`game:guidance.${guidanceLabel(state.guidance?.level ?? 0)}`),
          level: state.guidance?.level ?? 0,
        })}
      </div>
      {lastError && (
        <div
          style={{
            color: "#c47a5a",
            marginBottom: 12,
            padding: 8,
            background: "rgba(196,122,90,0.08)",
            border: "1px solid rgba(196,122,90,0.3)",
          }}
        >
          <div style={{ color: "#c47a5a", fontWeight: 700, marginBottom: 4 }}>
            {t("debug.lastError")}
          </div>
          <div style={{ wordBreak: "break-word" }}>{lastError}</div>
        </div>
      )}
      <div style={styles.mapTitle}>
        {t("debug.orphanKeys", { count: state.orphanKeys.length })}
      </div>
      {state.orphanKeys.map((o, i) => (
        <div key={i} style={styles.mapPuzzle}>
          · {o.hint || o.itemTag}
        </div>
      ))}
      <div style={{ ...styles.mapTitle, marginTop: 12 }}>
        {t("debug.orphanLocks", { count: state.orphanLocks.length })}
      </div>
      {state.orphanLocks.map((o, i) => (
        <div key={i} style={styles.mapPuzzle}>
          · {o.hint || o.requiresItemTag}
        </div>
      ))}
      <div style={{ ...styles.mapTitle, marginTop: 12 }}>
        {t("debug.inventoryTags")}
      </div>
      {state.inventory.map((id) => {
        const it = state.items[id];
        return (
          <div key={id} style={styles.mapPuzzle}>
            · {it?.name} [{it?.tags?.join(",")}]
          </div>
        );
      })}
      <div style={{ ...styles.mapTitle, marginTop: 12 }}>
        {t("debug.currentRoomSearched")}
      </div>
      {(state.rooms[state.currentRoom]?.searchedSpots || []).map((s, i) => (
        <div key={i} style={styles.mapPuzzle}>
          · {s}
        </div>
      ))}

      <div style={{ ...styles.mapTitle, marginTop: 12 }}>
        {t("debug.companions", { count: Object.keys(state.npcs).length })}
      </div>
      {Object.keys(state.npcs).length === 0 ? (
        <div
          style={{
            ...styles.mapPuzzle,
            fontStyle: "italic",
            opacity: 0.7,
          }}
        >
          {t("debug.noNpcsYet")}
        </div>
      ) : (
        Object.values(state.npcs).map((npc) => (
          <div
            key={npc.id}
            style={{
              ...styles.mapPuzzle,
              marginBottom: 6,
              paddingBottom: 6,
              borderBottom: "1px dashed var(--paper-edge, rgba(212,165,116,0.18))",
            }}
          >
            <div style={{ fontWeight: 600 }}>
              {npc.name}
              {npc.gender ? ` · ${npc.gender}` : ""}
              {npc.ageBand ? ` · ${npc.ageBand}` : ""}
              {npc.physique ? ` · ${npc.physique}` : ""}
            </div>
            <div style={{ opacity: 0.85 }}>
              aff {npc.affinity}
              {npc.archetype ? ` · ${npc.archetype}` : ""}
              {npc.disposition ? ` · ${npc.disposition}` : ""}
              {npc.intimacyPreference ? ` · ${npc.intimacyPreference}` : ""}
              {npc.openToShare ? ` · ${npc.openToShare}` : ""}
              {npc.mood ? ` · mood:${npc.mood}` : ""}
            </div>
            <div style={{ opacity: 0.7 }}>
              loc: {npc.location}
              {npc.speechPattern ? ` · ${npc.speechPattern}` : ""}
              {npc.humor ? ` · ${npc.humor}` : ""}
            </div>
            {npc.distinguishingFeature && (
              <div style={{ opacity: 0.7, fontStyle: "italic" }}>
                · {npc.distinguishingFeature}
              </div>
            )}
            {npc.tell && (
              <div style={{ opacity: 0.7, fontStyle: "italic" }}>
                tell: {npc.tell}
              </div>
            )}
            {npc.hiddenSecret && (
              <div style={{ opacity: 0.65 }}>
                secret: {npc.hiddenSecret}
              </div>
            )}
            {npc.trustScar && (
              <div style={{ opacity: 0.65 }}>
                scar: {npc.trustScar}
              </div>
            )}
            {npc.loyaltyPull && (
              <div style={{ opacity: 0.65 }}>
                loyalty: {npc.loyaltyPull}
              </div>
            )}
            {npc.bondMoments && npc.bondMoments.length > 0 && (
              <div style={{ opacity: 0.7 }}>
                bonds: {npc.bondMoments.join(", ")}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── Billing return screen (success / cancel from Stripe Checkout) ───────

// ── Boot loading screen (stories fetch in flight) ───────────────────────
// Reuses the centred-card aesthetic of BillingReturnScreen so we don't
// have to add new styles to styles.ts.
function BootLoadingScreen() {
  const { t } = useTranslation("game");
  return (
    <div style={styles.billingReturnShell}>
      <div style={styles.grain} />
      <div style={styles.billingReturnCard}>
        <div style={styles.modalEyebrow}>{t("boot.loadingEyebrow")}</div>
        <h1 style={styles.modalTitle}>{t("boot.loadingTitle")}</h1>
        <p style={styles.modalBody}>{t("boot.loadingBody")}</p>
      </div>
    </div>
  );
}

// ── Story service unreachable (boot fetch failed) ───────────────────────
// Same shell as the loading screen with a retry CTA. The full-page error
// is the "existing error pane" referenced in the dispatch — we adapt the
// phrasing to the new endpoint.
function StoryServiceUnreachableScreen() {
  const { t } = useTranslation(["game", "common"]);
  return (
    <div style={styles.billingReturnShell}>
      <div style={styles.grain} />
      <div style={styles.billingReturnCard}>
        <div style={styles.modalEyebrow}>{t("game:boot.unreachableEyebrow")}</div>
        <h1 style={styles.modalTitle}>{t("game:boot.unreachableTitle")}</h1>
        <p style={styles.modalBody}>{t("game:boot.unreachableBody")}</p>
        <button
          type="button"
          style={styles.modalCtaPrimary}
          onClick={() => {
            if (typeof window !== "undefined") window.location.reload();
          }}
        >
          {t("common:buttons.retry")}
        </button>
      </div>
    </div>
  );
}

function BillingReturnScreen({
  kind,
  onDismiss,
}: {
  kind: "success" | "cancel";
  onDismiss: () => void;
}) {
  const { t } = useTranslation("game");
  const isSuccess = kind === "success";
  return (
    <div style={styles.billingReturnShell}>
      <div style={styles.grain} />
      <div style={styles.billingReturnCard}>
        <div style={styles.modalEyebrow}>
          {isSuccess
            ? t("billingReturn.successEyebrow")
            : t("billingReturn.cancelEyebrow")}
        </div>
        <h1 style={styles.modalTitle}>
          {isSuccess
            ? t("billingReturn.successTitle")
            : t("billingReturn.cancelTitle")}
        </h1>
        <p style={styles.modalBody}>
          {isSuccess
            ? t("billingReturn.successBody")
            : t("billingReturn.cancelBody")}
        </p>
        <button
          type="button"
          style={styles.modalCtaPrimary}
          onClick={onDismiss}
        >
          {t("billingReturn.backToGame")}
        </button>
      </div>
    </div>
  );
}

function shortErr(msg: string): string {
  if (msg.length <= 60) return msg;
  return msg.slice(0, 57) + "…";
}

/**
 * Map the union-typed BootStatus value onto the `game.boot.*` key suffix.
 * Keeps the i18n lookup table flat (camelCase keys) without forcing the
 * caller to hard-code key strings.
 */
function bootStatusKey(status: BootStatus): string {
  switch (status) {
    case "ready":
      return "ready";
    case "loading save":
      return "loadingSave";
    case "loaded saved game":
      return "loadedSavedGame";
    case "fresh start":
      return "freshStart";
    case "save unreachable":
      return "saveUnreachable";
  }
}
